import React, { useState, useEffect, useRef } from 'react';
import ContextMenu from './ContextMenu';
import { scheduleLabel, normaliseSchedule, ScheduleEditor } from './ExecutionPanel';
import { getConnectorTypes, syncConnectorsNow } from '../../services/connectors';
import { tierPricing } from '../../utils/agentCost';
import { usePipeline } from './PipelineContext';
import { dataService } from '../../services/dataService';
import { PIPELINE_LABELS as pipeLabels, OPERATIONS_LABELS as opsLabels } from '../../constants/nodeLabels';
import { taskRecordFieldDefs } from '../../shared/projectlyContract';

const PERSON_EMOJIS = [
  '🧑','🧑🏻','🧑🏼','🧑🏽','🧑🏾','🧑🏿',
  '👨','👨🏻','👨🏼','👨🏽','👨🏾','👨🏿',
  '👩','👩🏻','👩🏼','👩🏽','👩🏾','👩🏿',
  '👨‍💼','👩‍💼','🧑‍💼','👨‍💻','👩‍💻','🧑‍💻',
  '👨‍🔬','👩‍🔬','🧑‍🔬','👨‍🎨','👩‍🎨','🧑‍🎨',
  '👨‍⚕️','👩‍⚕️','🧑‍⚕️','🤵','🤵‍♂️','🤵‍♀️',
  '👮','👮‍♂️','👮‍♀️','🧑‍🏫','👨‍🍳','👩‍🍳',
];

function EmojiPicker({ current, onSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div className="emoji-picker-wrap" ref={ref}>
      <button className="emoji-picker-trigger" onClick={() => setOpen(o => !o)} title="Change emoji" type="button">
        {current || '🧑'}
      </button>
      {open && (
        <div className="emoji-picker-grid">
          {PERSON_EMOJIS.map((em, i) => (
            <button key={i} className={`emoji-option${current === em ? ' active' : ''}`}
              onClick={() => { onSelect(em); setOpen(false); }} type="button">
              {em}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const FIELD_TYPES  = ['text', 'select', 'agent', 'reference', 'date', 'number'];

const MODEL_OPTS = [
  { id: 'fast',     icon: '⚡', label: 'Efficient', desc: 'Haiku 4.5 — fast, economical' },
  { id: 'standard', icon: '◈', label: 'Balanced',   desc: 'Sonnet 4.6 — general purpose' },
  { id: 'smart',    icon: '★',  label: 'Expert',     desc: 'Opus 4.8 — complex reasoning' },
];
const STATUS_COLORS = {
  'Open': '#3b82f6', 'In Progress': '#f59e0b', 'Resolved': '#10b981',
  'Done': '#10b981', 'Escalated': '#f43f5e', 'Backlog': '#94a3b8',
  'Review': '#8b5cf6',
};
const PRIORITY_COLORS = { 'Critical': '#f43f5e', 'High': '#f97316', 'Medium': '#f59e0b', 'Low': '#22c55e' };

const NOTE_COLORS = [
  { name: 'Yellow', value: '#fef9c3' }, { name: 'Blue', value: '#dbeafe' },
  { name: 'Green', value: '#dcfce7' }, { name: 'Pink', value: '#fce7f3' },
  { name: 'Lavender', value: '#ede9fe' }, { name: 'Peach', value: '#ffedd5' },
];

// ── Agent row with inline rename + expandable instructions ────────────────────
function AgentRow({ agent, onRename, onToggleManager, onRemove, onSetModel, onSetEmoji, onSetInstructions, opsMode, aiEnabled = true }) {
  const [editing,  setEditing]  = useState(false);
  const [name,     setName]     = useState(agent.name);
  const [expanded, setExpanded] = useState(false);
  const currentModel = agent.model || 'standard';

  const commit = () => { if (name.trim()) onRename(name.trim()); setEditing(false); };

  return (
    <div className="panel-agent-wrap">
      <div className="panel-agent-row">
        {opsMode && <EmojiPicker current={agent.emoji || '🧑'} onSelect={onSetEmoji} />}
        <div className="panel-agent-info">
          {editing ? (
            <input
              autoFocus
              className="panel-input agent-inline-input"
              value={name}
              onChange={e => setName(e.target.value)}
              onBlur={commit}
              onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
            />
          ) : (
            <span
              className="panel-agent-name"
              title="Click to rename"
              onClick={() => setEditing(true)}
            >{agent.name}</span>
          )}
          {agent.isManager && <span className="panel-manager-tag">{opsMode ? 'Head' : 'Manager'}</span>}
          {agent.role && <span className="panel-agent-role">{agent.role}</span>}
        </div>
        <div className="panel-agent-actions">
          {aiEnabled && (
            <div className="agent-model-select">
              {MODEL_OPTS.map(m => (
                <button
                  key={m.id}
                  className={`model-opt model-${m.id} ${currentModel === m.id ? 'active' : ''}`}
                  title={`${m.label} — ${m.desc}`}
                  onClick={() => onSetModel(m.id)}
                >{m.icon}</button>
              ))}
            </div>
          )}
          <button
            className={`panel-agent-expand-btn${agent.instructions ? ' has-instructions' : ''}`}
            title={expanded ? 'Hide instructions' : (agent.instructions ? 'View/edit instructions' : 'Add instructions')}
            onClick={() => setExpanded(e => !e)}
          >
            {expanded ? '▲' : (agent.instructions ? '✎' : '+')}
          </button>
          <button className="panel-agent-action" title={agent.isManager ? 'Remove manager' : 'Make manager'} onClick={() => onToggleManager()}>
            {agent.isManager ? '★' : '☆'}
          </button>
          <button className="panel-agent-remove" onClick={onRemove}>×</button>
        </div>
      </div>
      {expanded && (
        <div className="panel-agent-instructions">
          <label className="panel-label">
            {opsMode ? 'Job description' : 'Agent instructions'}
            {agent.instructions && <span className="panel-label-hint"> — used as AI system prompt</span>}
          </label>
          <textarea
            className="panel-textarea"
            rows={5}
            placeholder={opsMode
              ? 'Describe this employee\'s role, responsibilities, and how they should handle tasks…'
              : 'Detailed instructions for how this agent should think, respond, prioritise, and escalate…'}
            value={agent.instructions || ''}
            onChange={e => onSetInstructions?.(e.target.value)}
          />
        </div>
      )}
    </div>
  );
}

// ── Collection record table ───────────────────────────────────────────────────

/**
 * Turn a chosen operation + its filled-in fields into the three values the
 * engine actually executes. The picker is a UI over httpMethod/httpEndpoint/
 * httpBody — it never becomes a new action type, so pipelines built before it
 * existed keep working and nothing in the engine knows about operations.
 */
function buildRequestFromOperation(op, values) {
  if (!op) return null;
  let path = op.path;
  for (const p of op.params.filter(p => p.in === 'path')) {
    const v = values[p.id] ?? p.default ?? '';
    path = path.replace(`{${p.id}}`, String(v).trim());
  }
  const body = {};
  for (const p of op.params.filter(p => p.in === 'body')) {
    const raw = values[p.id];
    if (raw === undefined || raw === null || String(raw).trim() === '') continue;
    body[p.id] = p.type === 'number' ? Number(raw) : raw;
  }
  return {
    httpMethod: op.method,
    httpEndpoint: path,
    httpBody: Object.keys(body).length ? JSON.stringify(body, null, 2) : '',
  };
}

function CollectionPanel({ node, onUpdate, allNodes, onSendToAgents }) {
  const [recordCtx, setRecordCtx]   = useState(null);
  const [addingField, setAddingField] = useState(false);
  const [newField, setNewField]     = useState({ name: '', type: 'text', options: '' });
  const [addingRecord, setAddingRecord] = useState(false);
  const [newRecord, setNewRecord]   = useState({});
  const [records, setRecords]       = useState([]);
  const [linkProjects, setLinkProjects] = useState([]);
  const [connectorTypes, setConnectorTypes] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [syncNote, setSyncNote] = useState('');

  const { workspaceId } = usePipeline() || {};
  const fields = node.data.fields || [];
  const linked = node.data.linkedProjectId || '';
  const sourceType = node.data.source?.type || '';

  useEffect(() => {
    let alive = true;
    getConnectorTypes().then(list => { if (alive) setConnectorTypes(list); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!workspaceId) return;
    return dataService.subscribeRecords(workspaceId, node.id, (recs) => {
      setRecords(recs);
    });
  }, [workspaceId, node.id, linked]);

  // Projects for the "source" picker — and, when linked, keep the
  // materialised field definitions fresh (statuses / custom fields are
  // owned by the project and may have changed since linking).
  useEffect(() => {
    let alive = true;
    dataService.getProjects().then(list => {
      if (!alive) return;
      setLinkProjects(list);
      if (linked) {
        const p = list.find(x => x.id === linked);
        if (p) {
          const fresh = taskRecordFieldDefs(p);
          if (JSON.stringify(fresh) !== JSON.stringify(node.data.fields)) {
            onUpdate(node.id, { fields: fresh });
          }
        }
      }
    }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id, linked]);

  const setLink = (value) => {
    // One dropdown, three kinds of source: nothing, a project's tasks, or a
    // connector. They are mutually exclusive — a collection has exactly one
    // place its rows come from — so each branch clears the others.
    if (value.startsWith('connector:')) {
      const id = value.slice('connector:'.length);
      const conn = connectorTypes.find(c => c.id === id);
      onUpdate(node.id, {
        linkedProjectId: null,
        source: { type: id },
        // Fields come from the connector's own adapter, so the columns here
        // always match what the sync actually mirrors.
        fields: (conn?.fields || []).map(f => ({
          id: f.id, name: f.name, type: f.type,
          ...(f.options ? { options: f.options } : {}),
        })),
      });
      return;
    }
    if (!value) {
      // Unlink: the node keeps its field definitions but goes back to its own
      // (empty) records subcollection.
      onUpdate(node.id, { linkedProjectId: null, source: null });
      return;
    }
    const p = linkProjects.find(x => x.id === value);
    onUpdate(node.id, {
      linkedProjectId: value,
      source: null,
      fields: taskRecordFieldDefs(p || null),
    });
  };

  const runSync = async () => {
    if (!workspaceId) return;
    setSyncing(true); setSyncNote('');
    try {
      const res = await syncConnectorsNow(workspaceId);
      if (res.error) {
        setSyncNote(res.error);
      } else {
        const mine = (res.summaries || []).find(x => x.collectionId === node.id);
        // The server finds collections by reading the saved pipeline, so a node
        // that has not been persisted yet syncs nothing. Saying "Synced" there
        // sends the user looking for records that were never going to arrive.
        setSyncNote(mine
          ? `Synced — ${mine.created} new, ${mine.updated} changed, ${mine.unchanged} unchanged.${mine.truncated ? ' More rows exist than were returned.' : ''}`
          : 'Nothing synced — this collection is not in the saved pipeline yet. '
            + 'Wait for the canvas to show Saved (top right), then sync again.');
      }
    } catch (e) {
      setSyncNote(e.message || 'Sync failed.');
    }
    setSyncing(false);
  };

  const saveFields = (f) => onUpdate(node.id, { fields: f });

  const addField = () => {
    if (!newField.name.trim()) return;
    const f = {
      id: `f${Date.now()}`,
      name: newField.name.trim(),
      type: newField.type,
      options: newField.type === 'select' ? newField.options.split(',').map(o => o.trim()).filter(Boolean) : undefined,
    };
    saveFields([...fields, f]);
    setNewField({ name: '', type: 'text', options: '' });
    setAddingField(false);
  };

  const removeField = (fid) => saveFields(fields.filter(f => f.id !== fid));

  // Rows mirrored from a connector belong to the other app. Writing them here
  // would last until the next sync and no longer — the remote value wins — so
  // the write is refused rather than quietly discarded a minute later. Same
  // rule as the Data view; this panel is just the other way in.
  const remoteBacked = !!sourceType;

  const addRecord = () => {
    if (!workspaceId || remoteBacked) { setAddingRecord(false); return; }
    dataService.addRecord(workspaceId, node.id, { ...newRecord });
    setNewRecord({});
    setAddingRecord(false);
  };

  const removeRecord = (rid) => {
    if (!workspaceId || remoteBacked) return;
    dataService.deleteRecord(workspaceId, node.id, rid);
  };

  const updateRecordValue = (rid, fid, val) => {
    if (!workspaceId || remoteBacked) return;
    dataService.patchRecord(workspaceId, node.id, rid, { [fid]: val });
  };

  // Build record right-click menu
  const buildRecordMenu = (record) => {
    const functionNodes   = allNodes.filter(n => n.type === 'functionNode');
    const humanNodes      = allNodes.filter(n => n.type === 'humanNode');
    const collectionNodes = allNodes.filter(n => n.type === 'collectionNode' && n.id !== node.id);
    const agentField      = fields.find(f => f.type === 'agent');

    const items = [];

    if (functionNodes.length) {
      functionNodes.forEach(fn => {
        (fn.data.agents || []).forEach(agent => {
          items.push({
            icon: '👤',
            label: `Assign to ${agent.name} (${fn.data.label})`,
            onClick: () => {
              if (agentField) updateRecordValue(record.id, agentField.id, agent.name);
            }
          });
        });
      });
    }

    if (humanNodes.length) {
      humanNodes.forEach(h => {
        items.push({ icon: '🙋', label: `Assign to ${h.data.label}`, onClick: () => {
          if (agentField) updateRecordValue(record.id, agentField.id, h.data.label);
        }});
      });
    }

    if (items.length) items.push({ divider: true });

    if (collectionNodes.length) {
      collectionNodes.forEach(col => {
        items.push({ icon: '↗', label: `Escalate to ${col.data.label}`, onClick: () => {
          const statusField = fields.find(f => f.name.toLowerCase() === 'status');
          if (statusField) updateRecordValue(record.id, statusField.id, 'Escalated');
        }});
      });
      items.push({ divider: true });
    }

    items.push({ icon: '✏️', label: 'Edit record (inline)', soon: true });
    items.push({ divider: true });
    items.push({ icon: '🗑️', label: 'Delete record', danger: true, onClick: () => removeRecord(record.id) });
    return items;
  };

  return (
    <div className="panel-collection">
      {/* Source: its own records, or a project's tasks. Linking makes the
          project's task documents the records — same data in Projects, Data
          and the pipeline, no copies. */}
      <div className="panel-section">
        <div className="panel-section-header">
          <div className="panel-section-title">Source</div>
        </div>
        <select
          className="panel-input"
          data-testid="col-link-source"
          value={sourceType ? `connector:${sourceType}` : linked}
          onChange={e => setLink(e.target.value)}
        >
          <option value="">Standalone records</option>
          {linkProjects.map(p => (
            <option key={p.id} value={p.id}>
              🔗 {p.icon ? `${p.icon} ` : ''}{p.name || p.id} — tasks
            </option>
          ))}
          {connectorTypes.map(c => (
            <option key={c.id} value={`connector:${c.id}`}>
              🔌 {c.label} — live tasks
            </option>
          ))}
        </select>
        {sourceType && (
          <div className="col-linked-note">
            Rows are mirrored from {connectorTypes.find(c => c.id === sourceType)?.label || sourceType} every
            minute, using the token saved under Connectors. The other app stays the
            source of truth — edit rows there, not here. Actions that write back
            (an HTTP step pointed at this connector) change the real records.
            <div style={{ marginTop: 8 }}>
              <button className="panel-save-btn" disabled={syncing} onClick={runSync}>
                {syncing ? 'Syncing…' : 'Sync now'}
              </button>
            </div>
            {syncNote && <div style={{ marginTop: 6 }}>{syncNote}</div>}
          </div>
        )}
        {linked && (
          <div className="col-linked-note">
            Linked to the project's tasks — rows here <em>are</em> the tasks.
            Fields (statuses, custom fields) are managed on the project;
            triggers and actions on this collection act on real tasks.
          </div>
        )}
      </div>

      {/* Fields */}
      <div className="panel-section">
        <div className="panel-section-header">
          <div className="panel-section-title">Fields</div>
          {!linked && (
            <button className="panel-section-add-btn" onClick={() => setAddingField(a => !a)}>
              {addingField ? '×' : '+ Field'}
            </button>
          )}
        </div>

        <div className="col-fields-list">
          {fields.map(f => (
            <div key={f.id} className="col-field-row">
              <span className="col-field-name">{f.name}</span>
              <span className={`col-field-type type-${f.type}`}>{f.type}</span>
              {f.options && <span className="col-field-opts">{f.options.join(', ')}</span>}
              {!linked && <button className="col-field-remove" onClick={() => removeField(f.id)}>×</button>}
            </div>
          ))}
        </div>

        {addingField && !linked && (
          <div className="col-add-field-form">
            <input
              autoFocus
              className="panel-input"
              placeholder="Field name"
              value={newField.name}
              onChange={e => setNewField(p => ({ ...p, name: e.target.value }))}
            />
            <select
              className="panel-input"
              value={newField.type}
              onChange={e => setNewField(p => ({ ...p, type: e.target.value }))}
            >
              {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {newField.type === 'select' && (
              <input
                className="panel-input"
                placeholder="Options (comma-separated)"
                value={newField.options}
                onChange={e => setNewField(p => ({ ...p, options: e.target.value }))}
              />
            )}
            <button className="panel-add-agent-btn" onClick={addField}>Add field</button>
          </div>
        )}
      </div>

      {/* Records */}
      <div className="panel-section">
        <div className="panel-section-header">
          <div className="panel-section-title">Records <span className="record-count">{records.length}</span></div>
          <button className="panel-section-add-btn" onClick={() => setAddingRecord(a => !a)}>
            {addingRecord ? '×' : '+ Record'}
          </button>
        </div>

        {addingRecord && (
          <div className="col-add-record-form">
            {fields.map(f => (
              <div key={f.id} className="col-record-field-input">
                <label className="panel-label">{f.name}</label>
                {f.type === 'select' ? (
                  <select
                    className="panel-input"
                    value={newRecord[f.id] || ''}
                    onChange={e => setNewRecord(p => ({ ...p, [f.id]: e.target.value }))}
                  >
                    <option value="">— pick —</option>
                    {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input
                    className="panel-input"
                    placeholder={f.name}
                    value={newRecord[f.id] || ''}
                    onChange={e => setNewRecord(p => ({ ...p, [f.id]: e.target.value }))}
                  />
                )}
              </div>
            ))}
            <button className="panel-add-agent-btn" onClick={addRecord} disabled={remoteBacked}>Add record</button>
          </div>
        )}

        <div className="col-records">
          {records.length === 0 && <div className="panel-empty">No records yet.</div>}
          {records.map(record => {
            const statusField   = fields.find(f => f.name.toLowerCase() === 'status');
            const priorityField = fields.find(f => f.name.toLowerCase() === 'priority');
            const titleField    = fields.find(f => f.type === 'text');
            const agentField    = fields.find(f => f.type === 'agent');
            const status   = statusField   ? record.values?.[statusField.id]   : null;
            const priority = priorityField ? record.values?.[priorityField.id] : null;
            const title    = titleField    ? record.values?.[titleField.id]    : record.id;
            const assignee = agentField    ? record.values?.[agentField.id]    : null;

            return (
              <div
                key={record.id}
                className="col-record-row"
                onContextMenu={(e) => {
                  e.preventDefault();
                  setRecordCtx({ record, x: e.clientX, y: e.clientY });
                }}
                title="Right-click for options"
              >
                <div className="col-record-main">
                  <span className="col-record-title">{title || '—'}</span>
                  {assignee && <span className="col-record-assignee">→ {assignee}</span>}
                </div>
                <div className="col-record-badges">
                  {priority && (
                    <span className="col-badge" style={{ background: (PRIORITY_COLORS[priority] || '#94a3b8') + '22', color: PRIORITY_COLORS[priority] || '#94a3b8' }}>
                      {priority}
                    </span>
                  )}
                  {status && (
                    <span className="col-badge" style={{ background: (STATUS_COLORS[status] || '#94a3b8') + '22', color: STATUS_COLORS[status] || '#94a3b8' }}>
                      {status}
                    </span>
                  )}
                  {onSendToAgents && (
                    <button
                      className="col-trigger-btn"
                      title="Hand this record to the connected agent team to process in chat"
                      onClick={e => { e.stopPropagation(); onSendToAgents(node.id, record.id); }}
                    >→ Send to agent</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {recordCtx && (
        <ContextMenu
          x={recordCtx.x}
          y={recordCtx.y}
          items={buildRecordMenu(recordCtx.record)}
          onClose={() => setRecordCtx(null)}
        />
      )}
    </div>
  );
}

// ── Gate approval item ────────────────────────────────────────────────────────
function GateApprovalItem({ approval, onApprove, onReject }) {
  const [notes, setNotes] = useState('');
  return (
    <div className="gate-approval-item">
      <div className="gate-approval-action">{approval.action}</div>
      <div className="gate-approval-meta">
        from <strong>{approval.agentName || approval.nodeLabel || 'Agent'}</strong>
        {' · '}{new Date(approval.requestedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
      </div>
      {approval.description && <div className="gate-approval-desc">{approval.description}</div>}
      <textarea
        className="panel-textarea gate-approval-notes"
        placeholder="Notes for the agent (optional)…"
        value={notes}
        onChange={e => setNotes(e.target.value)}
        rows={2}
      />
      <div className="gate-approval-btns">
        <button className="gate-approve-btn" onClick={() => onApprove(approval.id, notes)}>✓ Approve</button>
        <button className="gate-reject-btn" onClick={() => onReject(approval.id, notes)}>✕ Reject</button>
      </div>
    </div>
  );
}

// ── Task schema section for Function nodes ───────────────────────────────────
function TaskSchemaSection({ node, onUpdate, opsMode }) {
  const [addingField, setAddingField] = useState(false);
  const [newField, setNewField] = useState({ name: '', type: 'text', options: '', required: false, defaultValue: '' });

  const taskFields = node.data.taskFields || [];
  const tasks      = node.data.tasks || [];
  const autoAssign = node.data.autoAssign || 'manual';

  const saveTaskFields = (f) => onUpdate(node.id, { taskFields: f });

  const addField = () => {
    if (!newField.name.trim()) return;
    const f = {
      id: `tf${Date.now()}`,
      name: newField.name.trim(),
      type: newField.type,
      options: newField.type === 'select' ? newField.options.split(',').map(o => o.trim()).filter(Boolean) : undefined,
      required: newField.required,
      defaultValue: newField.defaultValue.trim() || undefined,
    };
    saveTaskFields([...taskFields, f]);
    setNewField({ name: '', type: 'text', options: '', required: false, defaultValue: '' });
    setAddingField(false);
  };

  const removeField = (fid) => saveTaskFields(taskFields.filter(f => f.id !== fid));

  const moveField = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= taskFields.length) return;
    const next = [...taskFields];
    [next[index], next[target]] = [next[target], next[index]];
    saveTaskFields(next);
  };

  const toggleRequired = (fid) => {
    saveTaskFields(taskFields.map(f => f.id === fid ? { ...f, required: !f.required } : f));
  };

  const itemLabel = opsMode ? 'Work Item' : 'Task';
  const schemaLabel = opsMode ? 'Work Item Schema' : 'Task Schema';

  return (
    <div className="panel-section">
      <div className="panel-section-header">
        <div className="panel-section-title">
          {schemaLabel}
          <span className="record-count" style={{ background: '#7c3aed' }}>{tasks.length}</span>
        </div>
        <button className="panel-section-add-btn" onClick={() => setAddingField(a => !a)}>
          {addingField ? '×' : '+ Field'}
        </button>
      </div>

      <div className="task-schema-fields-list">
        {taskFields.map((f, i) => (
          <div key={f.id} className="task-schema-field-row">
            <div className="task-schema-field-main">
              <span className="task-schema-field-name">
                {f.name}
                {f.required && <span className="task-schema-required-badge">req</span>}
              </span>
              <span className={`col-field-type type-${f.type}`}>{f.type}</span>
              {f.options && <span className="col-field-opts">{f.options.join(', ')}</span>}
              {f.defaultValue && <span className="task-schema-default">= {f.defaultValue}</span>}
            </div>
            <div className="task-schema-field-actions">
              <button
                className="task-schema-move-btn"
                title="Move up"
                disabled={i === 0}
                onClick={() => moveField(i, -1)}
              >{'▲'}</button>
              <button
                className="task-schema-move-btn"
                title="Move down"
                disabled={i === taskFields.length - 1}
                onClick={() => moveField(i, 1)}
              >{'▼'}</button>
              <button
                className={`task-schema-req-btn${f.required ? ' active' : ''}`}
                title={f.required ? 'Mark as optional' : 'Mark as required'}
                onClick={() => toggleRequired(f.id)}
              >*</button>
              <button className="col-field-remove" onClick={() => removeField(f.id)}>{'×'}</button>
            </div>
          </div>
        ))}
        {taskFields.length === 0 && <div className="panel-empty">No {itemLabel.toLowerCase()} fields defined yet.</div>}
      </div>

      {addingField && (
        <div className="task-schema-add-form">
          <input
            autoFocus
            className="panel-input"
            placeholder="Field name"
            value={newField.name}
            onChange={e => setNewField(p => ({ ...p, name: e.target.value }))}
          />
          <select
            className="panel-input"
            value={newField.type}
            onChange={e => setNewField(p => ({ ...p, type: e.target.value }))}
          >
            {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          {newField.type === 'select' && (
            <input
              className="panel-input"
              placeholder="Options (comma-separated)"
              value={newField.options}
              onChange={e => setNewField(p => ({ ...p, options: e.target.value }))}
            />
          )}
          <input
            className="panel-input"
            placeholder="Default value (optional)"
            value={newField.defaultValue}
            onChange={e => setNewField(p => ({ ...p, defaultValue: e.target.value }))}
          />
          <label className="panel-checkbox-label">
            <input type="checkbox" checked={newField.required} onChange={e => setNewField(p => ({ ...p, required: e.target.checked }))} />
            <span>Required field</span>
          </label>
          <button className="panel-add-agent-btn" onClick={addField}>Add field</button>
        </div>
      )}

      <div className="task-schema-auto-assign">
        <label className="panel-label">Auto-assignment</label>
        <select
          className="panel-input"
          value={autoAssign}
          onChange={e => onUpdate(node.id, { autoAssign: e.target.value })}
        >
          <option value="manual">Manual — {opsMode ? 'head' : 'creator or lead'} assigns</option>
          <option value="round_robin">Round robin — distribute evenly</option>
          <option value="least_loaded">Least loaded — fewest active {opsMode ? 'work items' : 'tasks'}</option>
        </select>
      </div>
    </div>
  );
}

// ── Main NodePanel router ─────────────────────────────────────────────────────
export default function NodePanel({ node, allNodes, onUpdate, onDelete, onClose, opsMode, aiEnabled, onOpenGenerator, onGateApprove, onSendToAgents, onOpenTaskView }) {
  const pipelineCtx = usePipeline() || {};
  const canEdit = pipelineCtx.hasCapability ? pipelineCtx.hasCapability('edit_pipeline') : true;
  const canApprove = pipelineCtx.hasCapability ? pipelineCtx.hasCapability('approve') : true;
  const [label, setLabel] = useState(node.data.label || '');
  const [desc,  setDesc]  = useState(node.data.description || '');
  const [noteBody, setNoteBody] = useState(node.data.body || '');
  const [noteTitle, setNoteTitle] = useState(node.data.title || '');
  const [agentName, setAgentName] = useState('');
  const [agentRole, setAgentRole] = useState('');
  const [isManager, setIsManager] = useState(false);
  const [playbook,    setPlaybook]    = useState(node.data.playbook || '');
  const [ctxNotes,    setCtxNotes]    = useState(node.data.contextNotes || '');
  // Connection node fields
  const [connService,   setConnService]   = useState(node.data.serviceType || '');
  const [connConnector, setConnConnector] = useState(node.data.connectorId || '');
  const [connTypes,     setConnTypes]     = useState([]);

  useEffect(() => {
    let alive = true;
    getConnectorTypes().then(list => { if (alive) setConnTypes(list); });
    return () => { alive = false; };
  }, []);
  const [connUrl,       setConnUrl]       = useState(node.data.serviceUrl || '');
  const [connToken,     setConnToken]     = useState(node.data.authToken || '');
  const [connAuthType,  setConnAuthType]  = useState(node.data.authType || 'bearer');
  const [connHeaders,   setConnHeaders]   = useState(node.data.customHeaders || '');
  const [connTestStatus, setConnTestStatus] = useState(null);
  // Gate node fields
  const [approver,    setApprover]    = useState(node.data.approver || '');
  const [criteria,    setCriteria]    = useState(node.data.criteria || '');
  const [autoApprove, setAutoApprove] = useState(node.data.autoApprove || 'never');
  // Trigger node fields
  const [triggerType,       setTriggerType]       = useState(node.data.triggerType || 'manual');
  const [triggerCollection, setTriggerCollection] = useState(node.data.triggerCollectionId || '');
  const [triggerField,      setTriggerField]      = useState(node.data.triggerFieldId || '');
  const [triggerValue,      setTriggerValue]      = useState(node.data.triggerValue || '');
  // Extended trigger fields
  const [triggerFnId,        setTriggerFnId]        = useState(node.data.triggerFunctionId || '');
  const [triggerTaskField,   setTriggerTaskField]   = useState(node.data.triggerTaskFieldId || '');
  const [triggerTaskValue,   setTriggerTaskValue]   = useState(node.data.triggerTaskValue || '');
  // Which project this trigger acts on/for. Empty = workspace-wide (the
  // default, so existing pipelines are unaffected). walkWorkflow stamps this
  // onto everything the run produces so the Inbox can label it.
  const [triggerProjectId,   setTriggerProjectId]   = useState(node.data.projectId || '');
  const [triggerTaskStatus,  setTriggerTaskStatus]  = useState(node.data.triggerTaskStatus || '');
  const [wsProjects,         setWsProjects]         = useState([]);
  const [scheduleInterval,   setScheduleInterval]   = useState(node.data.scheduleInterval || '60');
  const [scheduleCron,       setScheduleCron]       = useState(node.data.scheduleCron || '');
  const [pollConnId,         setPollConnId]         = useState(node.data.pollConnectionId || '');
  const [pollEndpoint,       setPollEndpoint]       = useState(node.data.pollEndpoint || '');
  const [pollInterval,       setPollInterval]       = useState(node.data.pollInterval || '300');
  const [pollConditionField, setPollConditionField] = useState(node.data.pollConditionField || '');
  const [pollConditionValue, setPollConditionValue] = useState(node.data.pollConditionValue || '');
  const [webhookPath,        setWebhookPath]        = useState(node.data.webhookPath || '');
  // Condition node fields
  const [conditions,  setConditions]  = useState(node.data.conditions || []);
  const [condLogic,   setCondLogic]   = useState(node.data.logic || 'and');
  // Action node fields
  const [actionType,     setActionType]     = useState(node.data.actionType || 'update_field');
  const [actionColId,    setActionColId]    = useState(node.data.collectionId || '');
  const [actionFieldId,  setActionFieldId]  = useState(node.data.fieldId || '');
  const [actionFieldVal, setActionFieldVal] = useState(node.data.fieldValue || '');
  const [actionSubject,      setActionSubject]      = useState(node.data.notificationSubject || '');
  const [actionText,         setActionText]         = useState(node.data.notificationText || '');
  const [msgRecipientType,   setMsgRecipientType]   = useState(node.data.recipientType || 'owner');
  const [msgRecipientColId,  setMsgRecipientColId]  = useState(node.data.recipientCollectionId || '');
  const [msgRecipientFieldId,setMsgRecipientFieldId]= useState(node.data.recipientFieldId || '');
  const [msgRecipientAddress,setMsgRecipientAddress]= useState(node.data.recipientAddress || '');
  const [msgChannels,        setMsgChannels]        = useState(node.data.messageChannels || { inbox: true, email: false, whatsapp: false });
  const [actionFilterFieldId, setActionFilterFieldId] = useState(node.data.filterFieldId || '');
  const [actionFilterValue,   setActionFilterValue]   = useState(node.data.filterValue || '');
  // GitHub action fields
  const [ghRepo,     setGhRepo]     = useState(node.data.githubRepo || '');
  const [ghIssueTitle, setGhIssueTitle] = useState(node.data.issueTitle || '');
  const [ghIssueBody,  setGhIssueBody]  = useState(node.data.issueBody || '');
  const [ghIssueLabels, setGhIssueLabels] = useState(node.data.issueLabels || '');
  const [ghPrHead,   setGhPrHead]   = useState(node.data.prHead || '');
  const [ghPrBase,   setGhPrBase]   = useState(node.data.prBase || '');
  const [ghPrTitle,  setGhPrTitle]  = useState(node.data.prTitle || '');
  const [ghPrBody,   setGhPrBody]   = useState(node.data.prBody || '');
  const [ghFilePath, setGhFilePath] = useState(node.data.filePath || '');
  const [ghFileBranch, setGhFileBranch] = useState(node.data.fileBranch || '');
  const [ghFileContent, setGhFileContent] = useState(node.data.fileContent || '');
  const [ghCommitMsg, setGhCommitMsg] = useState(node.data.commitMessage || '');
  // Function task action fields
  const [actionFnId,         setActionFnId]         = useState(node.data.functionNodeId || '');
  const [taskFieldMap,       setTaskFieldMap]        = useState(node.data.taskFieldMap || {});
  const [taskUpdateFieldId,  setTaskUpdateFieldId]   = useState(node.data.taskUpdateFieldId || '');
  const [taskUpdateValue,    setTaskUpdateValue]     = useState(node.data.taskUpdateValue || '');
  const [taskFilterFieldId,  setTaskFilterFieldId]   = useState(node.data.taskFilterFieldId || '');
  const [taskFilterValue,    setTaskFilterValue]     = useState(node.data.taskFilterValue || '');
  // HTTP request action fields
  const [httpConnId,   setHttpConnId]   = useState(node.data.connectionNodeId || '');
  const [httpEndpoint, setHttpEndpoint] = useState(node.data.httpEndpoint || '');
  const [httpMethod,   setHttpMethod]   = useState(node.data.httpMethod || 'GET');
  const [httpBody,     setHttpBody]     = useState(node.data.httpBody || '');
  const [httpOpId,     setHttpOpId]     = useState(node.data.httpOperationId || '');
  const [httpOpParams, setHttpOpParams] = useState(node.data.httpOperationParams || {});
  const [httpRaw,      setHttpRaw]      = useState(!!node.data.httpRaw);
  const [agentFnId,    setAgentFnId]    = useState(node.data.agentFunctionId || '');
  const [agentPick,    setAgentPick]    = useState(node.data.agentId || '');
  const [agentTask,    setAgentTask]    = useState(node.data.agentTask || '');
  const [agentCap,     setAgentCap]     = useState(node.data.agentCapUsd ?? 2);
  const [httpHeaders,  setHttpHeaders]  = useState(node.data.httpHeaders || '');
  const [httpTimeoutSec, setHttpTimeoutSec] = useState(node.data.httpTimeoutSec ?? '15');
  const [httpRetries,    setHttpRetries]    = useState(node.data.httpRetries ?? '0');
  const [continueOnError, setContinueOnError] = useState(!!node.data.continueOnError);
  // Create record field mapping
  const [recordValues, setRecordValues] = useState(node.data.recordValues || {});
  const [dirty, setDirty] = useState(false);

  // Token field-picker for templatable message inputs: insert {field} at the
  // caret of whichever message field is focused (subject or body).
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const [activeMsgField, setActiveMsgField] = useState('body');
  const insertToken = (token) => {
    const isSubject = activeMsgField === 'subject';
    const el = isSubject ? subjectRef.current : bodyRef.current;
    const cur = isSubject ? actionSubject : actionText;
    const setter = isSubject ? setActionSubject : setActionText;
    const start = el?.selectionStart ?? cur.length;
    const end = el?.selectionEnd ?? cur.length;
    const next = cur.slice(0, start) + token + cur.slice(end);
    setter(next); setDirty(true);
    requestAnimationFrame(() => {
      if (el) { el.focus(); const pos = start + token.length; el.setSelectionRange(pos, pos); }
    });
  };

  useEffect(() => {
    setLabel(node.data.label || '');
    setDesc(node.data.description || '');
    setNoteBody(node.data.body || '');
    setNoteTitle(node.data.title || '');
    setPlaybook(node.data.playbook || '');
    setCtxNotes(node.data.contextNotes || '');
    setConnService(node.data.serviceType || '');
    setConnConnector(node.data.connectorId || '');
    setConnUrl(node.data.serviceUrl || '');
    setConnToken(node.data.authToken || '');
    setConnAuthType(node.data.authType || 'bearer');
    setConnHeaders(node.data.customHeaders || '');
    setConnTestStatus(null);
    setApprover(node.data.approver || '');
    setCriteria(node.data.criteria || '');
    setAutoApprove(node.data.autoApprove || 'never');
    setTriggerType(node.data.triggerType || 'manual');
    setTriggerCollection(node.data.triggerCollectionId || '');
    setTriggerField(node.data.triggerFieldId || '');
    setTriggerValue(node.data.triggerValue || '');
    setTriggerFnId(node.data.triggerFunctionId || '');
    setTriggerTaskField(node.data.triggerTaskFieldId || '');
    setTriggerTaskValue(node.data.triggerTaskValue || '');
    setTriggerProjectId(node.data.projectId || '');
    setTriggerTaskStatus(node.data.triggerTaskStatus || '');
    setScheduleInterval(node.data.scheduleInterval || '60');
    setScheduleCron(node.data.scheduleCron || '');
    setPollConnId(node.data.pollConnectionId || '');
    setPollEndpoint(node.data.pollEndpoint || '');
    setPollInterval(node.data.pollInterval || '300');
    setPollConditionField(node.data.pollConditionField || '');
    setPollConditionValue(node.data.pollConditionValue || '');
    setWebhookPath(node.data.webhookPath || '');
    setConditions(node.data.conditions || []);
    setCondLogic(node.data.logic || 'and');
    setActionType(node.data.actionType || 'update_field');
    setActionColId(node.data.collectionId || '');
    setActionFieldId(node.data.fieldId || '');
    setActionFieldVal(node.data.fieldValue || '');
    setActionSubject(node.data.notificationSubject || '');
    setActionText(node.data.notificationText || '');
    setMsgRecipientType(node.data.recipientType || 'owner');
    setMsgRecipientColId(node.data.recipientCollectionId || '');
    setMsgRecipientFieldId(node.data.recipientFieldId || '');
    setMsgRecipientAddress(node.data.recipientAddress || '');
    setMsgChannels(node.data.messageChannels || { inbox: true, email: false, whatsapp: false });
    setActionFilterFieldId(node.data.filterFieldId || '');
    setActionFilterValue(node.data.filterValue || '');
    setGhRepo(node.data.githubRepo || '');
    setGhIssueTitle(node.data.issueTitle || '');
    setGhIssueBody(node.data.issueBody || '');
    setGhIssueLabels(node.data.issueLabels || '');
    setGhPrHead(node.data.prHead || '');
    setGhPrBase(node.data.prBase || '');
    setGhPrTitle(node.data.prTitle || '');
    setGhPrBody(node.data.prBody || '');
    setGhFilePath(node.data.filePath || '');
    setGhFileBranch(node.data.fileBranch || '');
    setGhFileContent(node.data.fileContent || '');
    setGhCommitMsg(node.data.commitMessage || '');
    setActionFnId(node.data.functionNodeId || '');
    setTaskFieldMap(node.data.taskFieldMap || {});
    setTaskUpdateFieldId(node.data.taskUpdateFieldId || '');
    setTaskUpdateValue(node.data.taskUpdateValue || '');
    setTaskFilterFieldId(node.data.taskFilterFieldId || '');
    setTaskFilterValue(node.data.taskFilterValue || '');
    setHttpConnId(node.data.connectionNodeId || '');
    setHttpEndpoint(node.data.httpEndpoint || '');
    setHttpMethod(node.data.httpMethod || 'GET');
    setHttpBody(node.data.httpBody || '');
    setHttpOpId(node.data.httpOperationId || '');
    setHttpOpParams(node.data.httpOperationParams || {});
    setHttpRaw(!!node.data.httpRaw);
    setAgentFnId(node.data.agentFunctionId || '');
    setAgentPick(node.data.agentId || '');
    setAgentTask(node.data.agentTask || '');
    setAgentCap(node.data.agentCapUsd ?? 2);
    setHttpHeaders(node.data.httpHeaders || '');
    setHttpTimeoutSec(node.data.httpTimeoutSec ?? '15');
    setHttpRetries(node.data.httpRetries ?? '0');
    setContinueOnError(!!node.data.continueOnError);
    setRecordValues(node.data.recordValues || {});
    setDirty(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id]);

  // The trigger's project picker lists the workspace's real projects. One
  // fetch per opened trigger node is plenty — projects change rarely.
  useEffect(() => {
    if (node.type !== 'triggerNode') return;
    let alive = true;
    dataService.getProjects()
      .then(list => { if (alive) setWsProjects(list); })
      .catch(() => { if (alive) setWsProjects([]); });
    return () => { alive = false; };
  }, [node.id, node.type]);

  const save = () => {
    if (node.type === 'noteNode') {
      onUpdate(node.id, { title: noteTitle, body: noteBody });
    } else if (node.type === 'connectionNode') {
      onUpdate(node.id, { label, description: desc, serviceType: connService, connectorId: connConnector || null, serviceUrl: connUrl, authToken: connToken, authType: connAuthType, customHeaders: connHeaders, contextNotes: ctxNotes });
    } else if (node.type === 'gateNode') {
      onUpdate(node.id, { label, description: desc, approver, criteria, autoApprove, contextNotes: ctxNotes });
    } else if (node.type === 'triggerNode') {
      const triggerExtra = triggerType === 'function_task_change'
        ? { triggerFunctionId: triggerFnId, triggerTaskFieldId: triggerTaskField, triggerTaskValue }
        : triggerType === 'task_status_change'
        ? { triggerTaskStatus }
        : triggerType === 'schedule'
        ? { scheduleInterval, scheduleCron }
        : triggerType === 'poll_api'
        ? { pollConnectionId: pollConnId, pollEndpoint, pollInterval, pollConditionField, pollConditionValue }
        : triggerType === 'webhook'
        ? { webhookPath }
        : { triggerCollectionId: triggerCollection, triggerFieldId: triggerField, triggerValue };
      // projectId applies to EVERY trigger type: it scopes task-status
      // triggers and labels whatever any trigger's run produces.
      onUpdate(node.id, { label, description: desc, triggerType, projectId: triggerProjectId, ...triggerExtra });
    } else if (node.type === 'conditionNode') {
      onUpdate(node.id, { label, description: desc, conditions, logic: condLogic });
    } else if (node.type === 'actionNode') {
      const colNodes = (allNodes || []).filter(n => n.type === 'collectionNode');
      const aColNode = colNodes.find(n => n.id === actionColId);
      const aFields  = aColNode?.data?.fields || [];
      const fieldName       = aFields.find(f => f.id === actionFieldId)?.name || '';
      const filterFieldName = aFields.find(f => f.id === actionFilterFieldId)?.name || '';
      const extra = actionType === 'update_field'
        ? { collectionId: actionColId, fieldId: actionFieldId, fieldValue: actionFieldVal, fieldName, filterFieldId: actionFilterFieldId, filterValue: actionFilterValue, filterFieldName }
        : actionType === 'send_notification'
        ? {
            notificationSubject: actionSubject, notificationText: actionText,
            recipientType: msgRecipientType,
            recipientCollectionId: msgRecipientType === 'collection_field' ? msgRecipientColId : '',
            recipientFieldId: msgRecipientType === 'collection_field' ? msgRecipientFieldId : '',
            recipientAddress: msgRecipientType === 'specific' ? msgRecipientAddress : '',
            messageChannels: msgChannels,
          }
        : actionType === 'create_record'
        ? { collectionId: actionColId, recordValues }
        : actionType === 'github_create_issue'
        ? { githubRepo: ghRepo, issueTitle: ghIssueTitle, issueBody: ghIssueBody, issueLabels: ghIssueLabels }
        : actionType === 'github_create_pr'
        ? { githubRepo: ghRepo, prHead: ghPrHead, prBase: ghPrBase, prTitle: ghPrTitle, prBody: ghPrBody }
        : actionType === 'github_push_file'
        ? { githubRepo: ghRepo, filePath: ghFilePath, fileBranch: ghFileBranch, fileContent: ghFileContent, commitMessage: ghCommitMsg }
        : actionType === 'create_function_task'
        ? { functionNodeId: actionFnId, taskFieldMap }
        : actionType === 'update_function_task'
        ? { functionNodeId: actionFnId, taskUpdateFieldId, taskUpdateValue, taskFilterFieldId, taskFilterValue }
        : actionType === 'run_agent'
        ? { agentFunctionId: agentFnId, agentId: agentPick, agentTask, agentCapUsd: Number(agentCap) || 2 }
        : actionType === 'http_request'
        ? { connectionNodeId: httpConnId, httpEndpoint, httpMethod, httpBody, httpHeaders,
            httpOperationId: httpOpId, httpOperationParams: httpOpParams, httpRaw,
            httpTimeoutSec, httpRetries, continueOnError }
        : { collectionId: actionColId };
      onUpdate(node.id, { label, description: desc, actionType, ...extra });
    } else {
      onUpdate(node.id, { label, description: desc, playbook, contextNotes: ctxNotes });
    }
    setDirty(false);
  };

  const addAgent = () => {
    if (!agentName.trim()) return;
    const agents = [...(node.data.agents || []), {
      id: Date.now().toString(), name: agentName.trim(),
      role: agentRole.trim(), isManager
    }];
    onUpdate(node.id, { agents });
    setAgentName(''); setAgentRole(''); setIsManager(false);
  };

  const renameAgent = (agentId, name) => {
    const agents = (node.data.agents || []).map(a => a.id === agentId ? { ...a, name } : a);
    onUpdate(node.id, { agents });
  };

  const toggleManager = (agentId) => {
    const agents = (node.data.agents || []).map(a => a.id === agentId ? { ...a, isManager: !a.isManager } : a);
    onUpdate(node.id, { agents });
  };

  const removeAgent = (agentId) => {
    const agents = (node.data.agents || []).filter(a => a.id !== agentId);
    onUpdate(node.id, { agents });
  };

  const setAgentModel = (agentId, model) => {
    const agents = (node.data.agents || []).map(a => a.id === agentId ? { ...a, model } : a);
    onUpdate(node.id, { agents });
  };

  const setAgentEmoji = (agentId, emoji) => {
    const agents = (node.data.agents || []).map(a => a.id === agentId ? { ...a, emoji } : a);
    onUpdate(node.id, { agents });
  };

  const setAgentInstructions = (agentId, instructions) => {
    const agents = (node.data.agents || []).map(a => a.id === agentId ? { ...a, instructions } : a);
    onUpdate(node.id, { agents });
  };

  const nodeTypeLabel = (opsMode ? opsLabels : pipeLabels)[node.type] || node.type;
  const nodeTypeColor = { functionNode: 'var(--node-function)', humanNode: 'var(--node-human)', gateNode: 'var(--node-gate)', connectionNode: 'var(--node-connection)', noteNode: '#eab308', collectionNode: '#0d9488', triggerNode: '#7c3aed', conditionNode: '#0369a1', actionNode: '#b45309' }[node.type] || 'var(--accent-primary)';
  const accent = node.data.color || nodeTypeColor;

  return (
    <div className="node-panel">
      <div className="node-panel-header" style={{ borderBottomColor: accent + '44' }}>
        <div className="node-panel-type" style={{ color: accent }}>{nodeTypeLabel}</div>
        <button className="node-panel-close" onClick={onClose}>×</button>
      </div>

      <div className="node-panel-body">

        {!canEdit && (
          <div className="np-readonly-banner">
            🔒 You have read-only access to this pipeline. Editing is disabled for your role.
          </div>
        )}

        <fieldset className="np-edit-lock" disabled={!canEdit}>

        {/* ── Note editor ── */}
        {node.type === 'noteNode' && (
          <>
            <div className="panel-field">
              <label className="panel-label">Title</label>
              <input className="panel-input" value={noteTitle} placeholder="Short title…"
                onChange={e => { setNoteTitle(e.target.value); setDirty(true); }} />
            </div>
            <div className="panel-field">
              <label className="panel-label">Note</label>
              <textarea className="panel-textarea" rows={6} value={noteBody}
                onChange={e => { setNoteBody(e.target.value); setDirty(true); }} />
            </div>
            <div className="panel-field">
              <label className="panel-label">Color</label>
              <div className="note-color-picker">
                {NOTE_COLORS.map(c => (
                  <div
                    key={c.value}
                    className={`note-color-chip ${node.data.color === c.value ? 'active' : ''}`}
                    style={{ background: c.value }}
                    title={c.name}
                    onClick={() => onUpdate(node.id, { color: c.value })}
                  />
                ))}
              </div>
            </div>
            <div className="panel-field">
              <label className="panel-label">Assigned to <span className="cs-tag">Coming soon</span></label>
              <div className="panel-coming-soon-desc">Tag nodes that should act on this note.</div>
            </div>
          </>
        )}

        {/* ── Collection editor ── */}
        {node.type === 'collectionNode' && (
          <>
            {onOpenGenerator && (
              <button className="panel-generate-btn" onClick={() => onOpenGenerator(node.id, node.type, node.data.label)}>
                ✨ {node.data.fields?.length > 0 ? 'Edit with AI' : 'Generate with AI'}
              </button>
            )}
            <div className="panel-field">
              <label className="panel-label">Name</label>
              <input className="panel-input" value={label}
                onChange={e => { setLabel(e.target.value); setDirty(true); }} />
            </div>
            <div className="panel-field">
              <label className="panel-label">Description</label>
              <textarea className="panel-textarea" rows={2} value={desc}
                onChange={e => { setDesc(e.target.value); setDirty(true); }} />
            </div>
            <button className={`panel-save-btn${dirty ? '' : ' panel-save-idle'}`} onClick={save}>Save changes</button>
            <CollectionPanel node={node} onUpdate={onUpdate} allNodes={allNodes} onSendToAgents={onSendToAgents} />
          </>
        )}

        {/* ── Trigger node ── */}
        {node.type === 'triggerNode' && (() => {
          const colNodes  = (allNodes || []).filter(n => n.type === 'collectionNode');
          const fnNodes   = (allNodes || []).filter(n => n.type === 'functionNode');
          const connNodes = (allNodes || []).filter(n => n.type === 'connectionNode');
          const triggerColNode = colNodes.find(n => n.id === triggerCollection);
          const triggerFields  = triggerColNode?.data?.fields || [];
          const triggerFnNode  = fnNodes.find(n => n.id === triggerFnId);
          const fnTaskFields   = triggerFnNode?.data?.taskFields || [];
          return (
            <>
              <div className="panel-field">
                <label className="panel-label">Name</label>
                <input className="panel-input" value={label} placeholder="Trigger name"
                  onChange={e => { setLabel(e.target.value); setDirty(true); }} />
              </div>
              <div className="panel-field">
                <label className="panel-label">Description</label>
                <textarea className="panel-textarea" rows={2} value={desc} placeholder="What starts this workflow?"
                  onChange={e => { setDesc(e.target.value); setDirty(true); }} />
              </div>
              <div className="panel-field">
                <label className="panel-label">Trigger type</label>
                <select className="panel-input" value={triggerType}
                  onChange={e => { setTriggerType(e.target.value); setDirty(true); }}>
                  <option value="manual">▶ Manual — run by pressing the button</option>
                  <optgroup label="Data">
                    <option value="record_added">➕ Record added — fires when a new record appears</option>
                    <option value="field_change">✏️ Field changed — fires when a field matches a value</option>
                    <option value="function_task_change">📋 Task changed — fires when a task field changes</option>
                    <option value="task_status_change">📌 Project task status — fires when a project task moves to a status</option>
                  </optgroup>
                  <optgroup label="Timed">
                    <option value="schedule">🕐 Schedule — fires on an interval</option>
                  </optgroup>
                  <optgroup label="External (coming soon)">
                    <option value="poll_api" disabled>🔄 Poll API — check an endpoint on an interval (coming soon)</option>
                    <option value="webhook" disabled>🪝 Webhook — fires when an external call arrives (coming soon)</option>
                  </optgroup>
                </select>
              </div>
              {/* Project scope — offered on every trigger type. For a task-status
                  trigger it decides WHICH project's tasks are watched; for the
                  rest it labels the run's output (inbox items get the project
                  chip). Empty = workspace-wide, the default, so existing
                  pipelines behave exactly as before. */}
              <div className="panel-field">
                <label className="panel-label">Project</label>
                <select className="panel-input" data-testid="trigger-project" value={triggerProjectId}
                  onChange={e => { setTriggerProjectId(e.target.value); setDirty(true); }}>
                  <option value="">— whole workspace —</option>
                  {wsProjects.map(p => <option key={p.id} value={p.id}>{p.icon ? `${p.icon} ` : ''}{p.name || p.id}</option>)}
                </select>
              </div>
              {triggerType === 'task_status_change' && (
                <div className="panel-field">
                  <label className="panel-label">When status becomes</label>
                  {(() => {
                    const proj = wsProjects.find(p => p.id === triggerProjectId);
                    const statuses = Array.isArray(proj?.statuses) ? proj.statuses : [];
                    return statuses.length ? (
                      <select className="panel-input" data-testid="trigger-task-status" value={triggerTaskStatus}
                        onChange={e => { setTriggerTaskStatus(e.target.value); setDirty(true); }}>
                        <option value="">— any status change —</option>
                        {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : (
                      <input className="panel-input" data-testid="trigger-task-status" value={triggerTaskStatus}
                        placeholder="e.g. For warehouse (empty = any change)"
                        onChange={e => { setTriggerTaskStatus(e.target.value); setDirty(true); }} />
                    );
                  })()}
                </div>
              )}
              {(triggerType === 'record_added' || triggerType === 'field_change') && (
                <div className="panel-field">
                  <label className="panel-label">Watch collection</label>
                  <select className="panel-input" value={triggerCollection}
                    onChange={e => { setTriggerCollection(e.target.value); setTriggerField(''); setDirty(true); }}>
                    <option value="">— select collection —</option>
                    {colNodes.map(n => <option key={n.id} value={n.id}>{n.data.label || n.id}</option>)}
                  </select>
                </div>
              )}
              {triggerType === 'field_change' && triggerCollection && (
                <>
                  <div className="panel-field">
                    <label className="panel-label">Watch field</label>
                    <select className="panel-input" value={triggerField}
                      onChange={e => { setTriggerField(e.target.value); setDirty(true); }}>
                      <option value="">— select field —</option>
                      {triggerFields.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                    </select>
                  </div>
                  <div className="panel-field">
                    <label className="panel-label">Match value</label>
                    <input className="panel-input" value={triggerValue} placeholder="e.g. Done, Approved…"
                      onChange={e => { setTriggerValue(e.target.value); setDirty(true); }} />
                  </div>
                </>
              )}
              {triggerType === 'function_task_change' && (
                <>
                  <div className="panel-field">
                    <label className="panel-label">Watch {opsMode ? 'department' : 'function'}</label>
                    <select className="panel-input" value={triggerFnId}
                      onChange={e => { setTriggerFnId(e.target.value); setTriggerTaskField(''); setDirty(true); }}>
                      <option value="">— select {opsMode ? 'department' : 'function'} —</option>
                      {fnNodes.map(n => <option key={n.id} value={n.id}>{n.data.label || n.id}</option>)}
                    </select>
                  </div>
                  {triggerFnId && fnTaskFields.length > 0 && (
                    <>
                      <div className="panel-field">
                        <label className="panel-label">Watch task field</label>
                        <select className="panel-input" value={triggerTaskField}
                          onChange={e => { setTriggerTaskField(e.target.value); setDirty(true); }}>
                          <option value="">— any change —</option>
                          {fnTaskFields.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </select>
                      </div>
                      {triggerTaskField && (
                        <div className="panel-field">
                          <label className="panel-label">Match value</label>
                          {(() => {
                            const tf = fnTaskFields.find(f => f.id === triggerTaskField);
                            return tf?.type === 'select' ? (
                              <select className="panel-input" value={triggerTaskValue}
                                onChange={e => { setTriggerTaskValue(e.target.value); setDirty(true); }}>
                                <option value="">— any value —</option>
                                {(tf.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                              </select>
                            ) : (
                              <input className="panel-input" value={triggerTaskValue} placeholder="e.g. Done, Escalated…"
                                onChange={e => { setTriggerTaskValue(e.target.value); setDirty(true); }} />
                            );
                          })()}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
              {triggerType === 'schedule' && (
                <>
                  <div className="panel-field">
                    <label className="panel-label">Run every (seconds)</label>
                    <select className="panel-input" value={scheduleInterval}
                      onChange={e => { setScheduleInterval(e.target.value); setDirty(true); }}>
                      <option value="30">30 seconds</option>
                      <option value="60">1 minute</option>
                      <option value="300">5 minutes</option>
                      <option value="600">10 minutes</option>
                      <option value="900">15 minutes</option>
                      <option value="1800">30 minutes</option>
                      <option value="3600">1 hour</option>
                    </select>
                  </div>
                  <div className="panel-hint" style={{ padding: '4px 0', fontSize: 10 }}>
                    The workflow will fire automatically on this interval while the pipeline is active.
                  </div>
                </>
              )}
              {triggerType === 'poll_api' && (
                <>
                  <div className="panel-field">
                    <label className="panel-label">Connection</label>
                    <select className="panel-input" value={pollConnId}
                      onChange={e => { setPollConnId(e.target.value); setDirty(true); }}>
                      <option value="">— none (use full URL below) —</option>
                      {connNodes.map(n => <option key={n.id} value={n.id}>{n.data.label || n.id}{n.data.serviceUrl ? ` (${n.data.serviceUrl})` : ''}</option>)}
                    </select>
                  </div>
                  <div className="panel-field">
                    <label className="panel-label">{pollConnId ? 'Endpoint path' : 'Full URL'}</label>
                    <input className="panel-input" value={pollEndpoint}
                      placeholder={pollConnId ? '/api/status' : 'https://api.example.com/status'}
                      onChange={e => { setPollEndpoint(e.target.value); setDirty(true); }} />
                  </div>
                  <div className="panel-field">
                    <label className="panel-label">Poll every (seconds)</label>
                    <select className="panel-input" value={pollInterval}
                      onChange={e => { setPollInterval(e.target.value); setDirty(true); }}>
                      <option value="60">1 minute</option>
                      <option value="300">5 minutes</option>
                      <option value="600">10 minutes</option>
                      <option value="1200">20 minutes</option>
                      <option value="1800">30 minutes</option>
                      <option value="3600">1 hour</option>
                    </select>
                  </div>
                  <div className="panel-field">
                    <label className="panel-label">Fire when response contains field</label>
                    <input className="panel-input" value={pollConditionField} placeholder="e.g. status, data.ready"
                      onChange={e => { setPollConditionField(e.target.value); setDirty(true); }} />
                  </div>
                  {pollConditionField && (
                    <div className="panel-field">
                      <label className="panel-label">With value</label>
                      <input className="panel-input" value={pollConditionValue} placeholder="e.g. true, complete"
                        onChange={e => { setPollConditionValue(e.target.value); setDirty(true); }} />
                    </div>
                  )}
                </>
              )}
              {triggerType === 'webhook' && (
                <>
                  <div className="panel-field">
                    <label className="panel-label">Webhook identifier</label>
                    <input className="panel-input" value={webhookPath}
                      placeholder="e.g. my-trigger-123"
                      onChange={e => { setWebhookPath(e.target.value); setDirty(true); }} />
                  </div>
                  <div className="panel-hint" style={{ padding: '4px 0', fontSize: 10 }}>
                    External services can fire this trigger by calling your webhook URL with this identifier. The incoming payload will be available as template variables.
                  </div>
                </>
              )}
              <button className={`panel-save-btn${dirty ? '' : ' panel-save-idle'}`} onClick={save}>Save changes</button>
            </>
          );
        })()}

        {/* ── Condition node ── */}
        {node.type === 'conditionNode' && (() => {
          const colNodes = (allNodes || []).filter(n => n.type === 'collectionNode');
          const OPERATORS = [
            { value: 'eq', label: '= equals' }, { value: 'neq', label: '≠ not equals' },
            { value: 'contains', label: 'contains' }, { value: 'gt', label: '> greater than' },
            { value: 'gte', label: '≥ at least' }, { value: 'lt', label: '< less than' },
            { value: 'lte', label: '≤ at most' },
          ];

          const addCondition = () => setConditions(cs => [...cs, { id: `c${Date.now()}`, collectionId: '', fieldId: '', operator: 'eq', value: '', fieldName: '' }]);

          const updateCond = (cid, patch) => setConditions(cs => cs.map(c => {
            if (c.id !== cid) return c;
            const next = { ...c, ...patch };
            if (patch.collectionId) { next.fieldId = ''; next.fieldName = ''; }
            if (patch.fieldId) {
              const colNode = colNodes.find(n => n.id === next.collectionId);
              const field   = (colNode?.data?.fields || []).find(f => f.id === patch.fieldId);
              next.fieldName = field?.name || patch.fieldId;
            }
            return next;
          }));

          const removeCond = (cid) => setConditions(cs => cs.filter(c => c.id !== cid));

          return (
            <>
              <div className="panel-field">
                <label className="panel-label">Name</label>
                <input className="panel-input" value={label} placeholder="Condition name"
                  onChange={e => { setLabel(e.target.value); setDirty(true); }} />
              </div>
              <div className="panel-field">
                <label className="panel-label">Description</label>
                <textarea className="panel-textarea" rows={2} value={desc} placeholder="What does this check?"
                  onChange={e => { setDesc(e.target.value); setDirty(true); }} />
              </div>
              <div className="panel-section">
                <div className="panel-section-header">
                  <div className="panel-section-title">Conditions</div>
                  <div className="cond-logic-toggle">
                    <button className={`cond-logic-btn${condLogic === 'and' ? ' active' : ''}`}
                      onClick={() => { setCondLogic('and'); setDirty(true); }}>AND</button>
                    <button className={`cond-logic-btn${condLogic === 'or' ? ' active' : ''}`}
                      onClick={() => { setCondLogic('or'); setDirty(true); }}>OR</button>
                  </div>
                </div>
                {conditions.length === 0 && <div className="panel-empty">No conditions yet — add one below.</div>}
                {conditions.map((cond, i) => {
                  const condColNode = colNodes.find(n => n.id === cond.collectionId);
                  const condFields  = condColNode?.data?.fields || [];
                  const condField   = condFields.find(f => f.id === cond.fieldId);
                  return (
                    <div key={cond.id} className="cond-row">
                      {i > 0 && <div className="cond-logic-sep">{condLogic.toUpperCase()}</div>}
                      <div className="cond-row-inner">
                        <select className="panel-input cond-select" value={cond.collectionId}
                          onChange={e => { updateCond(cond.id, { collectionId: e.target.value }); setDirty(true); }}>
                          <option value="">Collection</option>
                          {colNodes.map(n => <option key={n.id} value={n.id}>{n.data.label || n.id}</option>)}
                        </select>
                        <select className="panel-input cond-select" value={cond.fieldId}
                          onChange={e => { updateCond(cond.id, { fieldId: e.target.value }); setDirty(true); }}>
                          <option value="">Field</option>
                          {condFields.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </select>
                        <select className="panel-input cond-select" value={cond.operator}
                          onChange={e => { updateCond(cond.id, { operator: e.target.value }); setDirty(true); }}>
                          {OPERATORS.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
                        </select>
                        {condField?.type === 'select' ? (
                          <select className="panel-input cond-select" value={cond.value}
                            onChange={e => { updateCond(cond.id, { value: e.target.value }); setDirty(true); }}>
                            <option value="">— pick —</option>
                            {(condField.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : (
                          <input className="panel-input cond-select" value={cond.value} placeholder="Value"
                            onChange={e => { updateCond(cond.id, { value: e.target.value }); setDirty(true); }} />
                        )}
                        <button className="cond-remove-btn" onClick={() => { removeCond(cond.id); setDirty(true); }}>×</button>
                      </div>
                    </div>
                  );
                })}
                <button className="panel-section-add-btn cond-add-btn" onClick={addCondition}>+ Add condition</button>
              </div>
              <button className={`panel-save-btn${dirty ? '' : ' panel-save-idle'}`} onClick={save}>Save changes</button>
            </>
          );
        })()}

        {/* ── Action node ── */}
        {node.type === 'actionNode' && (() => {
          const colNodes  = (allNodes || []).filter(n => n.type === 'collectionNode');
          const fnNodes   = (allNodes || []).filter(n => n.type === 'functionNode');
          const connNodes = (allNodes || []).filter(n => n.type === 'connectionNode');
          const actionColNode = colNodes.find(n => n.id === actionColId);
          const actionFields  = actionColNode?.data?.fields || [];
          const actionField   = actionFields.find(f => f.id === actionFieldId);
          const actionFnNode  = fnNodes.find(n => n.id === actionFnId);
          const fnTaskFields  = actionFnNode?.data?.taskFields || [];
          return (
            <>
              <div className="panel-field">
                <label className="panel-label">Name</label>
                <input className="panel-input" value={label} placeholder="Action name"
                  onChange={e => { setLabel(e.target.value); setDirty(true); }} />
              </div>
              <div className="panel-field">
                <label className="panel-label">Description</label>
                <textarea className="panel-textarea" rows={2} value={desc} placeholder="What does this step do?"
                  onChange={e => { setDesc(e.target.value); setDirty(true); }} />
              </div>
              <div className="panel-field">
                <label className="panel-label">Action type</label>
                <select className="panel-input" value={actionType}
                  onChange={e => { setActionType(e.target.value); setDirty(true); }}>
                  <option value="update_field">✏️ Update field — change a value in a collection</option>
                  <option value="send_notification">📨 Send message — notify via inbox, email, or WhatsApp</option>
                  <option value="create_record">➕ Create record — add a new row to a collection</option>
                  <optgroup label="Agents">
                    <option value="run_agent">🤖 Give it to an agent — hand a task to one of your team</option>
                  </optgroup>
                  <optgroup label="Function tasks">
                    <option value="create_function_task">📋 Create task — add a task to a function queue</option>
                    <option value="update_function_task">📝 Update task — change a task field value</option>
                  </optgroup>
                  <optgroup label="HTTP / API">
                    <option value="http_request">🌐 HTTP request — call an API via a connection</option>
                  </optgroup>
                  <optgroup label="GitHub (coming soon)">
                    <option value="github_create_issue" disabled>🐙 Create issue — open a GitHub issue (coming soon)</option>
                    <option value="github_create_pr" disabled>🔀 Create PR — open a pull request (coming soon)</option>
                    <option value="github_push_file" disabled>📄 Push file — commit a file to a branch (coming soon)</option>
                  </optgroup>
                </select>
              </div>
              {(actionType === 'update_field' || actionType === 'create_record') && (
                <div className="panel-field">
                  <label className="panel-label">Collection</label>
                  <select className="panel-input" value={actionColId}
                    onChange={e => { setActionColId(e.target.value); setActionFieldId(''); setActionFieldVal(''); setDirty(true); }}>
                    <option value="">— select collection —</option>
                    {colNodes.map(n => <option key={n.id} value={n.id}>{n.data.label || n.id}</option>)}
                  </select>
                </div>
              )}
              {actionType === 'create_record' && actionColId && actionFields.length > 0 && (
                <div className="panel-field">
                  <label className="panel-label">Record values <span className="panel-hint">Use {'{field_name}'} for dynamic values</span></label>
                  {actionFields.map(f => (
                    <div key={f.id} className="action-filter-row" style={{ marginBottom: 4 }}>
                      <span style={{ flex: '0 0 100px', fontSize: 11, color: 'var(--text-secondary)' }}>{f.name}</span>
                      {f.type === 'select' ? (
                        <select className="panel-input" value={recordValues[f.id] || ''}
                          onChange={e => { setRecordValues(rv => ({ ...rv, [f.id]: e.target.value })); setDirty(true); }}>
                          <option value="">—</option>
                          {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <input className="panel-input" value={recordValues[f.id] || ''}
                          placeholder={`Value for ${f.name}`}
                          onChange={e => { setRecordValues(rv => ({ ...rv, [f.id]: e.target.value })); setDirty(true); }} />
                      )}
                    </div>
                  ))}
                </div>
              )}
              {actionType === 'update_field' && actionColId && (
                <>
                  <div className="panel-field">
                    <label className="panel-label">Filter — only update records where <span className="panel-hint">(optional)</span></label>
                    <div className="action-filter-row">
                      <select className="panel-input" value={actionFilterFieldId}
                        onChange={e => { setActionFilterFieldId(e.target.value); setActionFilterValue(''); setDirty(true); }}>
                        <option value="">All records</option>
                        {actionFields.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                      </select>
                      {actionFilterFieldId && (() => {
                        const ff = actionFields.find(f => f.id === actionFilterFieldId);
                        return ff?.type === 'select' ? (
                          <select className="panel-input" value={actionFilterValue}
                            onChange={e => { setActionFilterValue(e.target.value); setDirty(true); }}>
                            <option value="">— equals —</option>
                            {(ff.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : (
                          <input className="panel-input" value={actionFilterValue} placeholder="equals…"
                            onChange={e => { setActionFilterValue(e.target.value); setDirty(true); }} />
                        );
                      })()}
                    </div>
                  </div>
                  <div className="panel-field">
                    <label className="panel-label">Set field</label>
                    <select className="panel-input" value={actionFieldId}
                      onChange={e => { setActionFieldId(e.target.value); setActionFieldVal(''); setDirty(true); }}>
                      <option value="">— select field —</option>
                      {actionFields.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                    </select>
                  </div>
                  {actionFieldId && (
                    <div className="panel-field">
                      <label className="panel-label">To value</label>
                      {actionField?.type === 'select' ? (
                        <select className="panel-input" value={actionFieldVal}
                          onChange={e => { setActionFieldVal(e.target.value); setDirty(true); }}>
                          <option value="">— pick —</option>
                          {(actionField.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <input className="panel-input" value={actionFieldVal} placeholder="New value"
                          onChange={e => { setActionFieldVal(e.target.value); setDirty(true); }} />
                      )}
                    </div>
                  )}
                </>
              )}
              {actionType === 'send_notification' && (
                <>
                  {/* ── WHO ── */}
                  <div className="panel-section-label">Who receives this?</div>
                  <div className="panel-field">
                    <label className="panel-label">Recipient</label>
                    <select className="panel-input" value={msgRecipientType}
                      onChange={e => { setMsgRecipientType(e.target.value); setDirty(true); }}>
                      <option value="owner">Pipeline owner (you)</option>
                      <option value="collection_field">From a collection field</option>
                      <option value="specific">Specific address</option>
                    </select>
                  </div>
                  {msgRecipientType === 'collection_field' && (
                    <>
                      <div className="panel-field">
                        <label className="panel-label">Collection</label>
                        <select className="panel-input" value={msgRecipientColId}
                          onChange={e => { setMsgRecipientColId(e.target.value); setMsgRecipientFieldId(''); setDirty(true); }}>
                          <option value="">— select collection —</option>
                          {colNodes.map(n => <option key={n.id} value={n.id}>{n.data.label || n.id}</option>)}
                        </select>
                      </div>
                      {msgRecipientColId && (() => {
                        const rcol = colNodes.find(n => n.id === msgRecipientColId);
                        const rFields = rcol?.data?.fields || [];
                        return (
                          <div className="panel-field">
                            <label className="panel-label">Email / phone field</label>
                            <select className="panel-input" value={msgRecipientFieldId}
                              onChange={e => { setMsgRecipientFieldId(e.target.value); setDirty(true); }}>
                              <option value="">— select field —</option>
                              {rFields.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                            </select>
                            <span className="panel-hint">Pick the field that contains the recipient's email or phone number.</span>
                          </div>
                        );
                      })()}
                    </>
                  )}
                  {msgRecipientType === 'specific' && (
                    <div className="panel-field">
                      <label className="panel-label">Address</label>
                      <input className="panel-input" value={msgRecipientAddress}
                        placeholder="email@example.com or +44 7700 900000"
                        onChange={e => { setMsgRecipientAddress(e.target.value); setDirty(true); }} />
                      <span className="panel-hint">Use <code>{'{field_name}'}</code> for dynamic values, or type a literal address.</span>
                    </div>
                  )}

                  {/* ── HOW ── */}
                  <div className="panel-section-label">Send via</div>
                  <div className="msg-channel-toggles">
                    {[
                      { key: 'inbox',    label: 'In-app inbox',  icon: '📥', always: true },
                      { key: 'email',    label: 'Email',         icon: '📧', always: false, hint: 'Requires SendGrid' },
                      { key: 'sms',      label: 'SMS',           icon: '📱', always: false, hint: 'Requires Twilio' },
                      { key: 'whatsapp', label: 'WhatsApp',      icon: '💬', always: false, hint: 'Requires Twilio' },
                    ].map(ch => (
                      <label key={ch.key} className={`msg-channel-toggle${msgChannels[ch.key] ? ' on' : ''}${ch.always ? ' always' : ''}`}>
                        <input type="checkbox" checked={msgChannels[ch.key]}
                          disabled={ch.always}
                          onChange={() => { if (!ch.always) { setMsgChannels(prev => ({ ...prev, [ch.key]: !prev[ch.key] })); setDirty(true); } }} />
                        <span className="msg-channel-icon">{ch.icon}</span>
                        <span className="msg-channel-name">{ch.label}</span>
                        {ch.hint && !msgChannels[ch.key] && <span className="msg-channel-hint">{ch.hint}</span>}
                      </label>
                    ))}
                  </div>

                  {/* ── WHAT ── */}
                  <div className="panel-section-label">Message content</div>
                  <div className="panel-field">
                    <label className="panel-label">Subject</label>
                    <input ref={subjectRef} className="panel-input" value={actionSubject} placeholder="e.g. Price updated for {name}"
                      onFocus={() => setActiveMsgField('subject')}
                      onChange={e => { setActionSubject(e.target.value); setDirty(true); }} />
                  </div>
                  <div className="panel-field">
                    <label className="panel-label">Message body</label>
                    <textarea ref={bodyRef} className="panel-textarea" rows={4} value={actionText}
                      placeholder="e.g. The price for {record.name} has changed to {record.price}."
                      onFocus={() => setActiveMsgField('body')}
                      onChange={e => { setActionText(e.target.value); setDirty(true); }} />
                  </div>
                  {(() => {
                    // Build the click-to-insert token list from the fields on the
                    // canvas, formatted exactly as the engine resolves them
                    // (lower-case, spaces -> underscores).
                    const names = new Set();
                    colNodes.forEach(c => (c.data?.fields || []).forEach(f => f.name && names.add(f.name)));
                    const tokens = [...[...names].map(n => `{${n.toLowerCase().replace(/\s+/g, '_')}}`), '{timestamp}', '{date}'];
                    return (
                      <div className="token-picker">
                        <span className="token-picker-label">Insert field (into {activeMsgField === 'subject' ? 'subject' : 'body'}):</span>
                        <div className="token-picker-chips">
                          {tokens.map(t => (
                            <button key={t} type="button" className="token-chip"
                              onMouseDown={e => e.preventDefault()}
                              onClick={() => insertToken(t)}>{t}</button>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  <div className="panel-hint-block">
                    Tokens are the field name in lower-case with spaces as underscores — e.g. "Customer Email" → <code>{'{customer_email}'}</code>. Click a chip above to insert one. Also available: <code>{'{timestamp}'}</code>, <code>{'{date}'}</code>, <code>{'{collection.count}'}</code>.
                  </div>
                </>
              )}
              {(actionType === 'github_create_issue' || actionType === 'github_create_pr' || actionType === 'github_push_file') && (
                <div className="panel-field">
                  <label className="panel-label">Repo override <span className="panel-hint">(leave blank to use workspace default)</span></label>
                  <input className="panel-input" value={ghRepo} placeholder="owner/repo"
                    onChange={e => { setGhRepo(e.target.value); setDirty(true); }} />
                </div>
              )}
              {actionType === 'github_create_issue' && (
                <>
                  <div className="panel-field">
                    <label className="panel-label">Issue title</label>
                    <input className="panel-input" value={ghIssueTitle} placeholder="Bug: something went wrong"
                      onChange={e => { setGhIssueTitle(e.target.value); setDirty(true); }} />
                  </div>
                  <div className="panel-field">
                    <label className="panel-label">Issue body</label>
                    <textarea className="panel-textarea" rows={4} value={ghIssueBody} placeholder="Describe the issue…"
                      onChange={e => { setGhIssueBody(e.target.value); setDirty(true); }} />
                  </div>
                  <div className="panel-field">
                    <label className="panel-label">Labels <span className="panel-hint">(comma-separated, optional)</span></label>
                    <input className="panel-input" value={ghIssueLabels} placeholder="bug, priority-high"
                      onChange={e => { setGhIssueLabels(e.target.value); setDirty(true); }} />
                  </div>
                </>
              )}
              {actionType === 'github_create_pr' && (
                <>
                  <div className="panel-field">
                    <label className="panel-label">Head branch</label>
                    <input className="panel-input" value={ghPrHead} placeholder="feat/my-feature"
                      onChange={e => { setGhPrHead(e.target.value); setDirty(true); }} />
                  </div>
                  <div className="panel-field">
                    <label className="panel-label">Base branch <span className="panel-hint">(merge into)</span></label>
                    <input className="panel-input" value={ghPrBase} placeholder="main"
                      onChange={e => { setGhPrBase(e.target.value); setDirty(true); }} />
                  </div>
                  <div className="panel-field">
                    <label className="panel-label">PR title</label>
                    <input className="panel-input" value={ghPrTitle} placeholder="Add feature X"
                      onChange={e => { setGhPrTitle(e.target.value); setDirty(true); }} />
                  </div>
                  <div className="panel-field">
                    <label className="panel-label">PR description</label>
                    <textarea className="panel-textarea" rows={4} value={ghPrBody} placeholder="What does this PR do?"
                      onChange={e => { setGhPrBody(e.target.value); setDirty(true); }} />
                  </div>
                </>
              )}
              {actionType === 'github_push_file' && (
                <>
                  <div className="panel-field">
                    <label className="panel-label">File path</label>
                    <input className="panel-input" value={ghFilePath} placeholder="src/utils/helper.js"
                      onChange={e => { setGhFilePath(e.target.value); setDirty(true); }} />
                  </div>
                  <div className="panel-field">
                    <label className="panel-label">Branch</label>
                    <input className="panel-input" value={ghFileBranch} placeholder="main"
                      onChange={e => { setGhFileBranch(e.target.value); setDirty(true); }} />
                  </div>
                  <div className="panel-field">
                    <label className="panel-label">Commit message</label>
                    <input className="panel-input" value={ghCommitMsg} placeholder="Update file via workflow"
                      onChange={e => { setGhCommitMsg(e.target.value); setDirty(true); }} />
                  </div>
                  <div className="panel-field">
                    <label className="panel-label">File content</label>
                    <textarea className="panel-textarea" rows={8} value={ghFileContent} placeholder="Full file content…"
                      onChange={e => { setGhFileContent(e.target.value); setDirty(true); }}
                      style={{ fontFamily: 'monospace', fontSize: 12 }} />
                  </div>
                </>
              )}
              {actionType === 'create_function_task' && (
                <>
                  <div className="panel-field">
                    <label className="panel-label">{opsMode ? 'Department' : 'Function'}</label>
                    <select className="panel-input" value={actionFnId}
                      onChange={e => { setActionFnId(e.target.value); setTaskFieldMap({}); setDirty(true); }}>
                      <option value="">— select {opsMode ? 'department' : 'function'} —</option>
                      {fnNodes.map(n => <option key={n.id} value={n.id}>{n.data.label || n.id}</option>)}
                    </select>
                  </div>
                  {actionFnId && fnTaskFields.length > 0 && (
                    <div className="panel-field">
                      <label className="panel-label">Field mapping <span className="panel-hint">Use {'{field_name}'} for dynamic values</span></label>
                      {fnTaskFields.map(tf => (
                        <div key={tf.id} className="action-filter-row" style={{ marginBottom: 4 }}>
                          <span style={{ flex: '0 0 100px', fontSize: 11, color: 'var(--text-secondary)' }}>{tf.name}</span>
                          {tf.type === 'select' ? (
                            <select className="panel-input" value={taskFieldMap[tf.id] || ''}
                              onChange={e => { setTaskFieldMap(m => ({ ...m, [tf.id]: e.target.value })); setDirty(true); }}>
                              <option value="">— {tf.default || 'auto'} —</option>
                              {(tf.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          ) : (
                            <input className="panel-input" value={taskFieldMap[tf.id] || ''}
                              placeholder={tf.default || `Value for ${tf.name}`}
                              onChange={e => { setTaskFieldMap(m => ({ ...m, [tf.id]: e.target.value })); setDirty(true); }} />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
              {actionType === 'update_function_task' && (
                <>
                  <div className="panel-field">
                    <label className="panel-label">{opsMode ? 'Department' : 'Function'}</label>
                    <select className="panel-input" value={actionFnId}
                      onChange={e => { setActionFnId(e.target.value); setTaskUpdateFieldId(''); setTaskFilterFieldId(''); setDirty(true); }}>
                      <option value="">— select {opsMode ? 'department' : 'function'} —</option>
                      {fnNodes.map(n => <option key={n.id} value={n.id}>{n.data.label || n.id}</option>)}
                    </select>
                  </div>
                  {actionFnId && fnTaskFields.length > 0 && (
                    <>
                      <div className="panel-field">
                        <label className="panel-label">Filter — only update tasks where <span className="panel-hint">(optional)</span></label>
                        <div className="action-filter-row">
                          <select className="panel-input" value={taskFilterFieldId}
                            onChange={e => { setTaskFilterFieldId(e.target.value); setTaskFilterValue(''); setDirty(true); }}>
                            <option value="">All tasks</option>
                            {fnTaskFields.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                          </select>
                          {taskFilterFieldId && (() => {
                            const ff = fnTaskFields.find(f => f.id === taskFilterFieldId);
                            return ff?.type === 'select' ? (
                              <select className="panel-input" value={taskFilterValue}
                                onChange={e => { setTaskFilterValue(e.target.value); setDirty(true); }}>
                                <option value="">— equals —</option>
                                {(ff.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                              </select>
                            ) : (
                              <input className="panel-input" value={taskFilterValue} placeholder="equals…"
                                onChange={e => { setTaskFilterValue(e.target.value); setDirty(true); }} />
                            );
                          })()}
                        </div>
                      </div>
                      <div className="panel-field">
                        <label className="panel-label">Set field</label>
                        <select className="panel-input" value={taskUpdateFieldId}
                          onChange={e => { setTaskUpdateFieldId(e.target.value); setDirty(true); }}>
                          <option value="">— select field —</option>
                          {fnTaskFields.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </select>
                      </div>
                      {taskUpdateFieldId && (
                        <div className="panel-field">
                          <label className="panel-label">To value <span className="panel-hint">Use {'{field_name}'} for dynamic values</span></label>
                          {(() => {
                            const tf = fnTaskFields.find(f => f.id === taskUpdateFieldId);
                            return tf?.type === 'select' ? (
                              <select className="panel-input" value={taskUpdateValue}
                                onChange={e => { setTaskUpdateValue(e.target.value); setDirty(true); }}>
                                <option value="">— pick —</option>
                                {(tf.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                              </select>
                            ) : (
                              <input className="panel-input" value={taskUpdateValue} placeholder="New value"
                                onChange={e => { setTaskUpdateValue(e.target.value); setDirty(true); }} />
                            );
                          })()}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
              {actionType === 'run_agent' && (() => {
                const fnNodes = (allNodes || []).filter(n => n.type === 'functionNode');
                const chosenFn = fnNodes.find(n => n.id === agentFnId);
                const agents = chosenFn?.data?.agents || [];
                return (
                <>
                  <div className="panel-field">
                    <label className="panel-label">Which team?</label>
                    <select className="panel-input" value={agentFnId}
                      onChange={e => { setAgentFnId(e.target.value); setAgentPick(''); setDirty(true); }}>
                      <option value="">— choose a team —</option>
                      {fnNodes.map(n => (
                        <option key={n.id} value={n.id}>
                          {n.data?.label || n.id} ({(n.data?.agents || []).length} agent{(n.data?.agents || []).length === 1 ? '' : 's'})
                        </option>
                      ))}
                    </select>
                    {fnNodes.length === 0 && (
                      <span className="panel-hint" style={{ fontSize: 10 }}>
                        Add a Function node to the canvas first — that is the team an agent belongs to.
                      </span>
                    )}
                  </div>

                  {chosenFn && (
                    <div className="panel-field">
                      <label className="panel-label">Which agent?</label>
                      <select className="panel-input" value={agentPick}
                        onChange={e => { setAgentPick(e.target.value); setDirty(true); }}>
                        <option value="">First available</option>
                        {agents.map(a => (
                          <option key={a.id} value={a.id}>
                            {a.name}{a.isManager ? ' (manager)' : ''} · {tierPricing(a.model).label}
                          </option>
                        ))}
                      </select>
                      {agents.length === 0 && (
                        <span className="panel-hint" style={{ fontSize: 10 }}>
                          This team has no agents yet. Add one on the Function node.
                        </span>
                      )}
                    </div>
                  )}

                  <div className="panel-field">
                    <label className="panel-label">What should it do?</label>
                    <textarea className="panel-textarea" rows={4} value={agentTask}
                      onChange={e => { setAgentTask(e.target.value); setDirty(true); }}
                      placeholder={'Look at {record.title} and explain what is wrong and how to fix it.'} />
                    <span className="panel-hint" style={{ fontSize: 10 }}>
                      Tokens like {'{record.title}'} are filled in when the pipeline runs. The agent is
                      already briefed with your knowledge base — write the task, not the background.
                    </span>
                  </div>

                  <div className="panel-field">
                    <label className="panel-label">Stop it at</label>
                    <input className="panel-input" type="number" min="0.05" max="50" step="0.05"
                      value={agentCap}
                      onChange={e => { setAgentCap(e.target.value); setDirty(true); }} />
                    <span className="panel-hint" style={{ fontSize: 10 }}>
                      A hard cap in dollars, enforced by Anthropic. The expensive failure is one agent
                      looping on something it cannot finish — keep this low enough that a runaway is
                      cheap, and raise it deliberately. It stops and tells you rather than half-finishing
                      silently.
                    </span>
                  </div>
                </>
                );
              })()}

              {actionType === 'http_request' && (() => {
                // Which connector is this action talking to? An explicit
                // Connection node wins; otherwise the connector-backed
                // collection on the canvas names it, so the common case needs
                // no Connection node at all.
                const selConn = (allNodes || []).find(n => n.id === httpConnId);
                const backedCols = (allNodes || []).filter(n => n.type === 'collectionNode' && n.data?.source?.type);
                const connectorId = selConn?.data?.connectorId
                  || (backedCols.length === 1 ? backedCols[0].data.source.type : '');
                const connMeta = connTypes.find(c => c.id === connectorId);
                const ops = connMeta?.operations || [];
                const op = ops.find(o => o.id === httpOpId);
                const guided = ops.length > 0 && !httpRaw;

                const applyOp = (opId, values) => {
                  const theOp = ops.find(o => o.id === opId);
                  const built = buildRequestFromOperation(theOp, values);
                  if (built) {
                    setHttpMethod(built.httpMethod);
                    setHttpEndpoint(built.httpEndpoint);
                    setHttpBody(built.httpBody);
                  }
                  setDirty(true);
                };

                const chooseOp = (opId) => {
                  const theOp = ops.find(o => o.id === opId);
                  const seeded = {};
                  (theOp?.params || []).forEach(pr => { if (pr.default) seeded[pr.id] = pr.default; });
                  setHttpOpId(opId);
                  setHttpOpParams(seeded);
                  applyOp(opId, seeded);
                };

                const setParam = (id, value) => {
                  const next = { ...httpOpParams, [id]: value };
                  setHttpOpParams(next);
                  applyOp(httpOpId, next);
                };

                return (
                <>
                  {guided && (
                    <>
                      <div className="panel-field">
                        <label className="panel-label">
                          What should this step do?
                          <span className="panel-hint"> via {connMeta.label}</span>
                        </label>
                        <select className="panel-input" value={httpOpId}
                          onChange={e => chooseOp(e.target.value)}>
                          <option value="">— choose an action —</option>
                          {ops.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                        </select>
                        {op?.description && <span className="panel-hint" style={{ fontSize: 10 }}>{op.description}</span>}
                      </div>

                      {op && op.params.map(pr => (
                        <div className="panel-field" key={pr.id}>
                          <label className="panel-label">
                            {pr.label}{pr.required ? ' *' : ''}
                          </label>
                          {pr.type === 'select' ? (
                            <select className="panel-input" value={httpOpParams[pr.id] ?? ''}
                              onChange={e => setParam(pr.id, e.target.value)}>
                              <option value="">— leave unchanged —</option>
                              {(pr.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          ) : pr.type === 'textarea' ? (
                            <textarea className="panel-textarea" rows={3} value={httpOpParams[pr.id] ?? ''}
                              onChange={e => setParam(pr.id, e.target.value)} />
                          ) : (
                            <input className="panel-input" type={pr.type === 'number' ? 'number' : 'text'}
                              value={httpOpParams[pr.id] ?? ''}
                              onChange={e => setParam(pr.id, e.target.value)} />
                          )}
                          {pr.hint && <span className="panel-hint" style={{ fontSize: 10 }}>{pr.hint}</span>}
                        </div>
                      ))}

                      {op && (
                        <div className="panel-field">
                          <span className="panel-hint" style={{ fontSize: 10 }}>
                            Sends <code>{httpMethod} {httpEndpoint}</code>
                          </span>
                        </div>
                      )}

                      <button type="button" className="panel-add-agent-btn"
                        onClick={() => { setHttpRaw(true); setDirty(true); }}>
                        Edit the raw request instead
                      </button>
                    </>
                  )}

                  {!guided && (
                  <>
                  {ops.length > 0 && (
                    <button type="button" className="panel-add-agent-btn"
                      onClick={() => { setHttpRaw(false); setDirty(true); }}>
                      ← Back to guided setup
                    </button>
                  )}
                  <div className="panel-field">
                    <label className="panel-label">Connection <span className="panel-hint">(optional — provides base URL + auth)</span></label>
                    <select className="panel-input" value={httpConnId}
                      onChange={e => { setHttpConnId(e.target.value); setDirty(true); }}>
                      <option value="">— none (use full URL below) —</option>
                      {connNodes.map(n => <option key={n.id} value={n.id}>{n.data.label || n.id}{n.data.serviceUrl ? ` (${n.data.serviceUrl})` : ''}</option>)}
                    </select>
                  </div>
                  <div className="panel-field">
                    <label className="panel-label">{httpConnId ? 'Endpoint path' : 'Full URL'}</label>
                    <input className="panel-input" value={httpEndpoint}
                      placeholder={httpConnId ? '/api/v1/resource' : 'https://api.example.com/endpoint'}
                      onChange={e => { setHttpEndpoint(e.target.value); setDirty(true); }} />
                  </div>
                  <div className="panel-field">
                    <label className="panel-label">Method</label>
                    <select className="panel-input" value={httpMethod}
                      onChange={e => { setHttpMethod(e.target.value); setDirty(true); }}>
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                      <option value="PUT">PUT</option>
                      <option value="PATCH">PATCH</option>
                      <option value="DELETE">DELETE</option>
                    </select>
                  </div>
                  {httpMethod !== 'GET' && httpMethod !== 'HEAD' && (
                    <div className="panel-field">
                      <label className="panel-label">Request body <span className="panel-hint">JSON — use {'{field_name}'} for templates</span></label>
                      <textarea className="panel-textarea" rows={5} value={httpBody}
                        placeholder={'{\n  "message": "{description}",\n  "status": "{status}"\n}'}
                        onChange={e => { setHttpBody(e.target.value); setDirty(true); }}
                        style={{ fontFamily: 'monospace', fontSize: 12 }} />
                    </div>
                  )}
                  </>
                  )}
                  <div className="panel-field">
                    <label className="panel-label">Extra headers <span className="panel-hint">JSON (optional)</span></label>
                    <textarea className="panel-textarea" rows={2} value={httpHeaders}
                      placeholder={'{"X-Custom": "value"}'}
                      onChange={e => { setHttpHeaders(e.target.value); setDirty(true); }}
                      style={{ fontFamily: 'monospace', fontSize: 12 }} />
                  </div>

                  {/* Resilience — external calls can be flaky; these cushion that. */}
                  <div className="panel-section-label">If the call is slow or fails</div>
                  <div className="panel-field panel-field-row">
                    <div style={{ flex: 1 }}>
                      <label className="panel-label">Timeout (seconds)</label>
                      <input className="panel-input" type="number" min="1" max="120" value={httpTimeoutSec}
                        onChange={e => { setHttpTimeoutSec(e.target.value); setDirty(true); }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label className="panel-label">Retries</label>
                      <select className="panel-input" value={httpRetries}
                        onChange={e => { setHttpRetries(e.target.value); setDirty(true); }}>
                        <option value="0">None</option>
                        <option value="1">1 retry</option>
                        <option value="2">2 retries</option>
                        <option value="3">3 retries</option>
                      </select>
                    </div>
                  </div>
                  <label className="panel-checkbox-row">
                    <input type="checkbox" checked={continueOnError}
                      onChange={e => { setContinueOnError(e.target.checked); setDirty(true); }} />
                    <span>Keep the pipeline running if this call fails<br />
                      <span className="panel-hint">Otherwise the whole run stops here. The failure is always recorded in Logs.</span>
                    </span>
                  </label>
                </>
                );
              })()}
              {(actionType === 'create_function_task' || actionType === 'update_function_task' || actionType === 'http_request'
                || actionType === 'send_notification' || actionType === 'github_create_issue' || actionType === 'github_create_pr'
                || actionType === 'github_push_file') && (
                <div className="panel-hint" style={{ padding: '6px 0', fontSize: 10 }}>
                  Tip: Use {'{field_name}'} in text fields to insert dynamic values from connected collections at runtime.
                </div>
              )}
              <button className={`panel-save-btn${dirty ? '' : ' panel-save-idle'}`} onClick={save}>Save changes</button>
            </>
          );
        })()}

        {/* ── Standard nodes ── */}
        {node.type !== 'noteNode' && node.type !== 'collectionNode' && node.type !== 'triggerNode' && node.type !== 'conditionNode' && node.type !== 'actionNode' && (
          <>
            {onOpenGenerator && (node.type === 'functionNode' || node.type === 'gateNode') && (
              <button
                className="panel-generate-btn"
                onClick={() => onOpenGenerator(node.id, node.type, node.data.label)}
              >
                ✨ {node.data.agents?.length > 0 || node.data.label ? 'Edit with AI' : 'Generate with AI'}
              </button>
            )}
            <div className="panel-field">
              <label className="panel-label">Name</label>
              <input className="panel-input" value={label}
                onChange={e => { setLabel(e.target.value); setDirty(true); }}
                placeholder="Node name" />
            </div>
            <div className="panel-field">
              <label className="panel-label">Description</label>
              <textarea className="panel-textarea" value={desc}
                onChange={e => { setDesc(e.target.value); setDirty(true); }}
                placeholder="What does this node do?" rows={3} />
            </div>
            <button className={`panel-save-btn${dirty ? '' : ' panel-save-idle'}`} onClick={save}>Save changes</button>

            {node.type === 'functionNode' && (
              <div className="panel-section">
                <div className="panel-section-title">{opsMode ? 'Employees' : 'Agents'}
                  <span className="panel-agent-hint">Click name to rename · Right-click chip on canvas for more</span>
                </div>
                {(node.data.agents || []).length === 0 && <div className="panel-empty">{opsMode ? 'No employees assigned yet.' : 'No agents assigned yet.'}</div>}
                {(node.data.agents || []).map(agent => (
                  <AgentRow
                    key={agent.id} agent={agent}
                    onRename={(name)         => renameAgent(agent.id, name)}
                    onToggleManager={()      => toggleManager(agent.id)}
                    onRemove={()             => removeAgent(agent.id)}
                    onSetModel={(model)      => setAgentModel(agent.id, model)}
                    onSetEmoji={(emoji)      => setAgentEmoji(agent.id, emoji)}
                    onSetInstructions={(ins) => setAgentInstructions(agent.id, ins)}
                    opsMode={opsMode}
                    aiEnabled={aiEnabled}
                  />
                ))}
                <div className="panel-add-agent">
                  <div className="panel-add-agent-title">{opsMode ? 'Add employee' : 'Add agent'}</div>
                  <input className="panel-input" placeholder={opsMode ? 'Name (e.g. Sarah)' : 'Name (e.g. David)'}
                    value={agentName} onChange={e => setAgentName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addAgent()} />
                  <input className="panel-input" placeholder="Role description (optional)"
                    value={agentRole} onChange={e => setAgentRole(e.target.value)}
                    style={{ marginTop: 6 }} />
                  <label className="panel-checkbox-label">
                    <input type="checkbox" checked={isManager} onChange={e => setIsManager(e.target.checked)} />
                    <span>{opsMode ? 'Department Head (oversees team)' : 'Manager (oversees others in this node)'}</span>
                  </label>
                  <button className="panel-add-agent-btn" onClick={addAgent}>{opsMode ? '+ Add employee' : '+ Add agent'}</button>
                </div>
              </div>
            )}

            {node.type === 'functionNode' && (
              <>
                <div className="panel-section">
                  <button
                    className="panel-add-agent-btn"
                    style={{ width: '100%', marginBottom: 0 }}
                    onClick={() => onOpenTaskView?.(node.id)}
                  >
                    📋 {opsMode ? 'View work items' : 'View task queue'}{(node.data.tasks?.length || 0) > 0 ? ` (${node.data.tasks.length})` : ''}
                  </button>
                </div>
                <TaskSchemaSection node={node} onUpdate={onUpdate} opsMode={opsMode} />
              </>
            )}

            {/* ── Gate approval rules ── */}
            {node.type === 'gateNode' && (
              <div className="panel-section">
                <div className="panel-section-title">Approval rules</div>
                <div className="panel-field">
                  <label className="panel-label">Approver</label>
                  <input
                    className="panel-input"
                    placeholder="e.g. Marketing Manager, Business Owner…"
                    value={approver}
                    onChange={e => { setApprover(e.target.value); setDirty(true); }}
                  />
                </div>
                <div className="panel-field">
                  <label className="panel-label">Approval criteria</label>
                  <textarea
                    className="panel-textarea"
                    rows={3}
                    placeholder="What conditions must be met for approval?"
                    value={criteria}
                    onChange={e => { setCriteria(e.target.value); setDirty(true); }}
                  />
                </div>
                <div className="panel-field">
                  <label className="panel-label">Auto-approve after</label>
                  <select
                    className="panel-input"
                    value={autoApprove}
                    onChange={e => { setAutoApprove(e.target.value); setDirty(true); }}
                  >
                    <option value="never">Never — always require manual approval</option>
                    <option value="24h">24 hours if no action</option>
                    <option value="48h">48 hours if no action</option>
                    <option value="72h">72 hours if no action</option>
                  </select>
                </div>
              </div>
            )}

            {/* ── Connection config ── */}
            {node.type === 'connectionNode' && (
              <div className="panel-section">
                <div className="panel-section-title">Connection config</div>
                <div className="panel-field">
                  <label className="panel-label">Use a saved connector</label>
                  <select
                    className="panel-input"
                    value={connConnector}
                    onChange={e => { setConnConnector(e.target.value); setDirty(true); }}
                  >
                    <option value="">— none, configure manually below —</option>
                    {connTypes.map(c => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                  <span className="panel-hint" style={{ fontSize: 10 }}>
                    Takes the token and base URL from Connectors, so the credential
                    is not copied into this pipeline. Leave unset to enter them by hand.
                  </span>
                </div>
                <div className="panel-field">
                  <label className="panel-label">Service type</label>
                  <select
                    className="panel-input"
                    value={connService}
                    onChange={e => { setConnService(e.target.value); setDirty(true); }}
                  >
                    <option value="">— select service —</option>
                    <option value="github">GitHub</option>
                    <option value="slack">Slack</option>
                    <option value="jira">Jira</option>
                    <option value="gmail">Gmail</option>
                    <option value="notion">Notion</option>
                    <option value="airtable">Airtable</option>
                    <option value="linear">Linear</option>
                    <option value="twitter">Twitter / X</option>
                    <option value="discord">Discord</option>
                    <option value="webhook">Webhook / Custom HTTP</option>
                    <option value="custom">Other / Custom</option>
                  </select>
                </div>
                <div className="panel-field">
                  <label className="panel-label">Base URL</label>
                  <input
                    className="panel-input"
                    placeholder="https://api.example.com"
                    value={connUrl}
                    onChange={e => { setConnUrl(e.target.value); setDirty(true); }}
                  />
                  <span className="panel-hint" style={{ fontSize: 10 }}>Actions and triggers will append their endpoint paths to this base URL.</span>
                </div>
                <div className="panel-field">
                  <label className="panel-label">Auth type</label>
                  <select
                    className="panel-input"
                    value={connAuthType}
                    onChange={e => { setConnAuthType(e.target.value); setDirty(true); }}
                  >
                    <option value="bearer">Bearer token</option>
                    <option value="api_key">API key (query param)</option>
                    <option value="basic">Basic auth</option>
                    <option value="header">Custom header</option>
                    <option value="none">None</option>
                  </select>
                </div>
                {connAuthType !== 'none' && (
                  <div className="panel-field">
                    <label className="panel-label">
                      {connAuthType === 'bearer' ? 'Bearer token' : connAuthType === 'api_key' ? 'API key' : connAuthType === 'basic' ? 'Username:Password' : 'Auth value'}
                    </label>
                    <input
                      className="panel-input"
                      type="password"
                      placeholder={connAuthType === 'basic' ? 'user:pass' : 'Token or key…'}
                      value={connToken}
                      onChange={e => { setConnToken(e.target.value); setDirty(true); }}
                    />
                  </div>
                )}
                <div className="panel-field">
                  <label className="panel-label">Custom headers <span className="panel-hint">(JSON, optional)</span></label>
                  <textarea
                    className="panel-textarea"
                    rows={2}
                    placeholder={'{"X-Custom": "value"}'}
                    value={connHeaders}
                    onChange={e => { setConnHeaders(e.target.value); setDirty(true); }}
                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                  />
                </div>
                {connUrl && (
                  <div className="panel-field">
                    <button
                      className="panel-add-agent-btn"
                      style={{ width: '100%' }}
                      onClick={async () => {
                        setConnTestStatus('testing');
                        try {
                          const headers = {};
                          if (connAuthType === 'bearer' && connToken) headers['Authorization'] = `Bearer ${connToken}`;
                          else if (connAuthType === 'api_key' && connToken) headers['X-API-Key'] = connToken;
                          else if (connAuthType === 'basic' && connToken) headers['Authorization'] = `Basic ${btoa(connToken)}`;
                          if (connHeaders.trim()) {
                            try { Object.assign(headers, JSON.parse(connHeaders)); } catch {}
                          }
                          const resp = await fetch(connUrl, { method: 'HEAD', headers });
                          setConnTestStatus(resp.ok ? 'success' : `error: HTTP ${resp.status}`);
                        } catch (e) {
                          setConnTestStatus(`error: ${e.message}`);
                        }
                        setTimeout(() => setConnTestStatus(null), 5000);
                      }}
                    >
                      {connTestStatus === 'testing' ? 'Testing...'
                        : connTestStatus?.startsWith('error') ? connTestStatus
                        : connTestStatus === 'success' ? 'Connected!'
                        : 'Test connection'}
                    </button>
                    {connTestStatus === 'success' && (
                      <span style={{ color: '#10b981', fontSize: 11, marginTop: 4, display: 'block' }}>Connection successful</span>
                    )}
                    {connTestStatus?.startsWith('error') && (
                      <span style={{ color: '#f43f5e', fontSize: 11, marginTop: 4, display: 'block' }}>{connTestStatus}</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {node.type === 'functionNode' && (
              <div className="panel-section">
                <div className="panel-section-title">{opsMode ? 'Handbook' : 'Playbook'}</div>
                <textarea
                  className="panel-textarea"
                  rows={5}
                  placeholder={opsMode
                    ? 'Instructions employees follow in this department — processes, escalation rules, tone of communication…'
                    : 'Instructions agents follow in this function — task rules, escalation conditions, tone, what to do when stuck…'}
                  value={playbook}
                  onChange={e => { setPlaybook(e.target.value); setDirty(true); }}
                />
              </div>
            )}

            <div className="panel-section">
              <div className="panel-section-title">Context notes</div>
              <textarea
                className="panel-textarea"
                rows={3}
                placeholder="Extra context for this node — edge cases, constraints, links, or anything agents or humans should know…"
                value={ctxNotes}
                onChange={e => { setCtxNotes(e.target.value); setDirty(true); }}
              />
            </div>

            {node.type === 'functionNode' && (
              <div className="panel-section">
                <div className="panel-section-title">{opsMode ? 'Activation' : 'Execution'}</div>
                <div className="exec-inline-row">
                  <div className={`exec-status-dot${node.data.active !== false ? ' on' : ''}`} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)', flex: 1 }}>
                    {node.data.active !== false ? 'Active' : 'Inactive'}
                    {node.data.schedule ? ` · ${scheduleLabel(normaliseSchedule(node.data.schedule))}` : ' · Inherits pipeline'}
                  </span>
                  <button
                    className={`panel-toggle${node.data.active !== false ? ' on' : ''}`}
                    onClick={() => onUpdate(node.id, { active: node.data.active === false })}
                  >
                    <span className="panel-toggle-knob" />
                  </button>
                </div>
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
                    Custom schedule — overrides the pipeline schedule for this {opsMode ? 'department' : 'function'}.
                    Set to <em>Triggered only</em> to inherit the pipeline schedule.
                  </div>
                  <ScheduleEditor
                    value={node.data.schedule || null}
                    onChange={(s) => onUpdate(node.id, { schedule: s })}
                    allowNull
                  />
                </div>
              </div>
            )}
          </>
        )}
        </fieldset>

        {/* ── Gate pending approvals — gated by the `approve` capability, kept
            outside the read-only edit lock so Approvers can act on them. ── */}
        {node.type === 'gateNode' && canApprove && (node.data.pendingApprovals?.length > 0) && (
          <div className="panel-section gate-approvals-section">
            <div className="panel-section-title">
              Pending approvals
              <span className="record-count">{node.data.pendingApprovals.length}</span>
            </div>
            {node.data.pendingApprovals.map(a => (
              <GateApprovalItem
                key={a.id}
                approval={a}
                onApprove={(id, notes) => onGateApprove?.(node.id, id, 'approved', notes)}
                onReject={(id, notes) => onGateApprove?.(node.id, id, 'rejected', notes)}
              />
            ))}
          </div>
        )}
      </div>

      {canEdit && node.type !== 'collectionNode' && node.type !== 'triggerNode' && node.type !== 'conditionNode' && node.type !== 'actionNode' && (
        <div className="node-panel-footer">
          {dirty && node.type === 'noteNode' && (
            <button className="panel-save-btn" style={{ marginBottom: 8 }} onClick={save}>{opsMode ? 'Save memo' : 'Save note'}</button>
          )}
          <button className="panel-delete-btn" onClick={() => onDelete(node.id)}>
            {opsMode ? ({
              functionNode:   'Remove department',
              humanNode:      'Remove stakeholder',
              gateNode:       'Remove approval',
              connectionNode: 'Remove app',
              noteNode:       'Remove memo',
              triggerNode:    'Remove automation start',
              conditionNode:  'Remove decision',
              actionNode:     'Remove auto step',
            }[node.type] || 'Delete') : 'Delete node'}
          </button>
        </div>
      )}

      {canEdit && node.type === 'collectionNode' && (
        <div className="node-panel-footer">
          <button className="panel-delete-btn" onClick={() => onDelete(node.id)}>{opsMode ? 'Remove records' : 'Delete collection'}</button>
        </div>
      )}

      {canEdit && (node.type === 'triggerNode' || node.type === 'conditionNode' || node.type === 'actionNode') && (
        <div className="node-panel-footer">
          <button className="panel-delete-btn" onClick={() => onDelete(node.id)}>
            {opsMode ? ({
              triggerNode: 'Remove automation start', conditionNode: 'Remove decision', actionNode: 'Remove auto step',
            }[node.type] || 'Delete') : 'Delete node'}
          </button>
        </div>
      )}
    </div>
  );
}
